import { mediaRef } from '@/services/mediaAssets';
import { galleryBySlug } from '../../../adventureGalleries';
import { defineReckonEvent } from '../../schema';

const coastalWalkImage = mediaRef('adventures/sydney-bondi-coogee-coastline.jpg');
const barTorinoImage = mediaRef('adventures/sydney-bar-torino.jpg');
const blueMountainsImage = mediaRef('adventures/sydney-blue-mountains.jpg');
const manlyBeachImage = mediaRef('adventures/sydney-manly-beach-crew-selfie.jpg');
const marrickvilleHotelImage = mediaRef('adventures/sydney-marrickville-hotel.jpg');
const bondiTrattoriaImage = mediaRef('adventures/sydney-bondi-trattoria.jpg');
const quayDinnerImage = mediaRef('adventures/sydney-quay-team-dinner.jpg');
const rooftopPubImage = mediaRef('adventures/sydney-pubs-opera-house-rooftop.jpg');
const scenicWorldRailwayImage = mediaRef('adventures/sydney-scenic-world-railway.jpg');
const heritageHotelImage = mediaRef('adventures/sydney-australian-heritage-hotel.jpg');
const chapelDinnerImage = mediaRef('adventures/sydney-20-chapel-team-dinner.jpg');
const fourPinesImage = mediaRef('adventures/sydney-4-pines-team-lunch.jpg');
const featherdaleImage = mediaRef('adventures/sydney-featherdale.jpg');
const mountainCultureImage = mediaRef('adventures/sydney-mountain-culture-beer.jpg');
const margaretDinnerImage = mediaRef('adventures/sydney-margaret-team-dinner.jpg');
const pubTableImage = mediaRef('adventures/sydney-pubs-outdoor-table.jpg');
const teddySwimsKeynoteImage = mediaRef('adventures/sydney-teddy-swims-keynote.jpg');
const wSydneyHotelImage = mediaRef('adventures/sydney-w-hotel.jpg');

const legacy = galleryBySlug['sxsw-sydney-2025'];

const reference = (entityId, order, dayLabel, eventDescription, extra = {}) => ({
  entityId,
  order,
  dayLabel,
  eventDescription,
  ...extra,
});

const cardImage = (id, src, alt, position = 'center center', orientation = 'landscape') => ({
  id,
  src,
  alt,
  position,
  orientation,
});

const sxswSydney2025 = defineReckonEvent({
  id: 'sxsw-sydney-2025',
  slug: 'sxsw-sydney-2025',
  title: 'SXSW Sydney 2025',
  subtitle: 'SXSW brought us to Sydney. The city made the trip unforgettable.',
  eventType: 'conference',
  status: 'published',
  startDate: '2025-10-13',
  endDate: '2025-10-19',
  year: 2025,
  destination: 'Sydney',
  city: 'Sydney',
  region: 'New South Wales',
  country: 'Australia',
  heroImage: {
    id: 'sxsw-sydney-hero',
    src: legacy.heroImage,
    alt: 'Reckon heroes overlooking Sydney Harbour at sunset',
    orientation: 'landscape',
  },
  summary: legacy.summary,
  atAGlance: [
    {
      id: 'destination', label: 'Destination', value: 'Sydney, Australia', icon: 'mdi-map-marker-outline',
    },
    {
      id: 'dates', label: 'Dates', value: 'October 13–19, 2025', icon: 'mdi-calendar-range',
    },
    {
      id: 'crew', label: 'Crew', value: 6, suffix: ' travelers', icon: 'mdi-account-group-outline',
    },
    {
      id: 'home-base', label: 'Home Base', value: 'W Sydney', icon: 'mdi-bed-outline',
    },
  ],
  tripByNumbers: [
    { id: 'time-zones', label: 'Time zones crossed', value: 15 },
    { id: 'team-dinners', label: 'Team dinners', value: 4 },
    { id: 'dim-sum-firsts', label: 'First-time dim sum experiences', value: 5 },
    { id: 'concerts', label: 'Live concerts', value: 'Daily' },
    { id: 'pubs', label: 'Pubs visited', value: 'Lost count' },
    { id: 'photos', label: 'Photos taken', value: 'Thousands' },
  ],
  experienceDNA: [
    {
      id: 'food', label: 'Food', score: 100, icon: 'mdi-silverware-fork-knife',
    },
    {
      id: 'adventure', label: 'Adventure', score: 60, icon: 'mdi-compass-outline',
    },
    {
      id: 'family', label: 'Family', score: 0, icon: 'mdi-human-male-female-child',
    },
    {
      id: 'nightlife', label: 'Nightlife', score: 70, icon: 'mdi-music-note',
    },
    {
      id: 'nature', label: 'Nature', score: 60, icon: 'mdi-pine-tree',
    },
    {
      id: 'culture', label: 'Culture', score: 80, icon: 'mdi-theater',
    },
    {
      id: 'relaxation', label: 'Relaxation', score: 40, icon: 'mdi-weather-sunset',
    },
    {
      id: 'luxury', label: 'Luxury', score: 60, icon: 'mdi-star-outline',
    },
    {
      id: 'conference', label: 'Conference', score: 100, icon: 'mdi-presentation',
    },
  ],
  conference: {
    conferenceName: 'SXSW Sydney',
    venueNames: ['Darling Harbour', 'Tumbalong Park', 'Sydney conference venues'],
    conferenceDates: 'October 13–19, 2025',
    purpose: 'Learn from creators and technologists, explore the expo, make new connections, and experience how Sydney carried the conference into the city around it.',
    attendanceReason: 'SXSW brought together technology, entrepreneurship, music, and culture. We spent the formal conference hours in sessions and the rest of the week learning from Sydney itself.',
    memorableSessions: ['Teddy Swims keynote', 'Mo Gawdat keynote', 'Niall Firth on AI in 2026', 'Opening sessions', 'Expo Hall', 'Live concerts at Tumbalong Park'],
  },
  sectionContent: {
    venues: {
      title: 'The Conference Across Sydney',
      description: 'Darling Harbour and Tumbalong Park made the conference feel connected to the city around it.',
    },
    dining: {
      description: 'Coffee, team dinners, historic pubs, breweries, and neighborhood discoveries kept the story moving between sessions.',
    },
  },
  journeyStrip: [
    { id: 'day-1', label: 'Day 1', value: 'Arrival, Coogee to Bondi, and the first team dinner' },
    { id: 'day-2', label: 'Day 2', value: 'SXSW, Quay, The Rocks, and historic pubs' },
    { id: 'day-3', label: 'Day 3', value: 'Conference, dim sum, Marrickville, and 20 Chapel' },
    { id: 'day-4', label: 'Day 4', value: 'Sessions, Manly ferry, beach, and local beer' },
    { id: 'day-5', label: 'Day 5', value: 'Cocktails at Bar Torino and a final team dinner at Margaret' },
    { id: 'day-6', label: 'Day 6', value: 'Featherdale, the Blue Mountains, Scenic World, and Mountain Culture' },
  ],
  sessions: [
    reference('teddy-swims-keynote', 20, 'Conference week', 'One of the sessions that stayed with the crew beyond the conference itself.', {
      cardImage: cardImage('teddy-swims-keynote-card', teddySwimsKeynoteImage, 'Teddy Swims speaking onstage during his SXSW Sydney keynote', 'center center', 'portrait'),
      whatWeRemember: 'The keynote brought the creative side of SXSW into focus and became one of the week’s shared reference points.',
      featured: true,
    }),
    reference('mo-gawdat-human-advantage-keynote', 21, 'Conference week', 'Mo Gawdat explored why our humanity becomes more valuable, not less, as AI grows more capable.', {
      whatWeRemember: 'The session reframed AI as a reason to invest more deeply in the qualities that make us human.',
      featured: true,
    }),
    reference('niall-firth-ai-in-2026', 22, 'Conference week', 'Niall Firth looked ahead to the technologies, shifts, and questions likely to define AI in 2026.', {
      whatWeRemember: 'The session gave the crew a practical horizon for thinking about where AI is headed next.',
    }),
  ],
  venues: [
    reference('sxsw-sydney', 10, 'Days 2–4', 'Opening sessions, the expo hall, networking events, team coffee breaks, and new ideas filled the middle of each day.'),
    reference('tumbalong-park', 11, 'Every afternoon', 'Live concerts turned the end of each conference day into a shared outdoor experience.', {
      whatWeRemember: 'The music made the boundary between conference programming and the city disappear.',
      featured: true,
    }),
  ],
  places: [
    reference('bondi-beach', 11, 'Day 1', 'The coastal walk from Coogee ended at one of Australia’s most recognizable beaches.', {
      cardImage: cardImage('bondi-beach-card', coastalWalkImage, 'Rocky coastline along the walk from Coogee to Bondi Beach'),
      detailSectionId: 'activities',
      detailEntityId: 'coogee-bondi-walk',
      detailLabel: 'Explore the Bondi Beach Story',
    }),
    reference('the-rocks', 12, 'Days 1, 2 & 4', 'Our first-night stop at the Fortune of War Hotel introduced us to The Rocks. We returned for a Day 2 pub crawl and stopped there again after the Manly ferry outing.', {
      cardImage: cardImage('the-rocks-card', rooftopPubImage, 'The Reckon crew at a rooftop pub overlooking Sydney Harbour'),
      detailSectionId: 'dining',
      detailEntityId: 'fortune-of-war',
      detailLabel: 'Read the Fortune of War Story',
    }),
    reference('manly-beach', 13, 'Day 4', 'A ferry ride across Sydney Harbour led to an afternoon of ocean air, beach time, fish and chips, and local craft beer.', {
      cardImage: cardImage('manly-beach-card', manlyBeachImage, 'The Reckon crew together at Manly Beach'),
      detailSectionId: 'activities',
      detailEntityId: 'manly-ferry',
      detailLabel: 'Explore the Manly Beach Story',
    }),
    reference('blue-mountains', 14, 'Day 6', 'Rainforest, the Three Sisters, and Jamison Valley revealed another side of New South Wales beyond Sydney Harbour.', {
      cardImage: cardImage('blue-mountains-place-card', blueMountainsImage, 'The Reckon crew overlooking the Three Sisters in the Blue Mountains', 'center center', 'portrait'),
      detailSectionId: 'activities',
      detailEntityId: 'blue-mountains',
      detailLabel: 'Explore the Blue Mountains Story',
    }),
  ],
  activities: [
    reference('coogee-bondi-walk', 1, 'Day 1', 'We had just flown to the other side of the world. After an inordinate amount of time sitting on airplanes, we landed in Sydney in the morning, went straight to the hotel and dropped off our bags. Not all of our rooms were ready yet. That was probably a good thing. The last thing we needed was for everyone to disappear into a hotel room, lie down for a minute and wake up six hours later with absolutely no idea what day it was. I had another idea. Let’s go stretch our legs. In retrospect, choosing the Coogee to Bondi Coastal Walk as our definition of “stretching our legs” may have been slightly ambitious.', {
      cardImage: cardImage('coogee-bondi-card', coastalWalkImage, 'Rocky coastline along the Coogee to Bondi coastal walk'),
      storyTitle: 'Stretching Our Legs',
      detailLabel: 'Read the Coogee to Bondi Story',
      whyWeWent: 'To shake off the long flight and begin experiencing Sydney before the conference started.',
      whatHappened: 'The walk coincided with Sculpture by the Sea, turning the coastal trail into a free outdoor gallery.',
      whatWeRemember: 'Every bend revealed another artwork against the Pacific Ocean.',
      relatedBadgeIds: ['bondi-to-coogee-coastal-walk'],
      featured: true,
      storySections: [
        {
          heading: 'Start at Coogee',
          paragraphs: [
            'The entire crew piled into a taxi and headed toward Coogee.',
            'The ride took a little while.',
            'Once we got away from the center of Sydney, the city transitioned into mostly residential streets and suburbs. As we got closer to the coast, the terrain began doing some of the navigating for us.',
            'The roads started heading downhill.',
            'Somewhere down there was the ocean.',
            'Our taxi eventually dropped us near the middle of Coogee Beach at the Rainbow Walkway.',
            'It was a pretty spectacular introduction.',
            'A series of broad, arched steps descended toward the beach almost like an amphitheater.',
            'Except the coastline was the stage.',
            'People were scattered across the steps, sitting, talking and taking in the morning. On the landing before the final steps to the beach, the pavement had been painted like a rainbow.',
            'We had been in Australia for only a few hours.',
            'This was where our walk began.',
          ],
        },
        {
          heading: 'Up Into the Hills',
          paragraphs: [
            'We headed north along Coogee Beach.',
            'When the beach ended, the walking path began climbing into the hills and winding along the coastline.',
            'The surroundings felt surprisingly ordinary and extraordinary at the same time.',
            'The path moved through beautifully maintained parkland. People were out running. Others were walking their dogs. People were simply enjoying a spring morning.',
            'It felt like a neighborhood park.',
            'Except a few steps away were cliffs dropping toward the Pacific Ocean.',
            'As we climbed, the views opened behind us. Coogee became smaller while the coastline stretched farther into the distance.',
            'For the people who lived there, this was apparently just somewhere to take the dog for a walk.',
            'For seven guys who had gotten off an airplane that morning, it was something else entirely.',
          ],
        },
        {
          heading: 'Down to Gordon’s Bay',
          paragraphs: [
            'At Gordon’s Bay, the walk changed again.',
            'The paved path gave way to wooden boardwalk-like stairs that carried us down toward the small beach tucked into the head of the bay.',
            'The water seemed calmer there, protected from the open ocean.',
            'What I remember most was the light.',
            'It was still late morning, and the sun hitting the water inside the bay was striking enough that we stopped for a few photos.',
            'Then we kept moving.',
            'There wasn’t much time to enjoy being down near sea level because the path almost immediately started climbing again on the opposite side.',
            'Our little post-flight leg stretch was developing a pattern.',
            'Down to the water.',
            'Back up the cliffs.',
            'Repeat.',
          ],
        },
        {
          heading: 'Clovelly',
          paragraphs: [
            'The next major change was Clovelly Beach.',
            'Instead of another wide crescent of sand exposed to the ocean, Clovelly sat at the end of a long, narrow inlet.',
            'The path brought us alongside an ocean pool stretching along the side of the inlet, with the protected beach tucked all the way at the back.',
            'Compared with the open Pacific we’d been walking beside, it looked remarkably calm.',
            'It also looked like a nearly perfect place to bring kids.',
            'Then the path carried us onward and upward again.',
          ],
        },
        {
          heading: 'Not a Bad Final View',
          paragraphs: [
            'Eventually we reached Waverley Cemetery.',
            'There was something almost surreal about finding such an enormous cemetery occupying prime real estate on the cliffs above the Pacific.',
            'Rows of monuments and headstones sat against this huge backdrop of ocean and sky.',
            'I remember looking around and thinking that if I ever had to choose a final resting place, this wouldn’t be too far from the top of my list.',
            'There are worse views to have forever.',
          ],
        },
        {
          heading: 'Down to Bronte',
          paragraphs: [
            'At Bronte, the path dropped us back toward beach level.',
            'We followed the stairs down and found ourselves walking right alongside the sand.',
            'People were stretched out only a short distance from the walking path, catching the same late-morning spring sun that had been following us up the coast.',
            'That was one of the things I liked about the walk.',
            'It wasn’t a trail separated from Sydney.',
            'It moved directly through the way people used the coastline.',
            'Running.',
            'Swimming.',
            'Walking dogs.',
            'Lying on the beach.',
            'And occasionally watching seven jet-lagged Americans repeatedly climb and descend stairs because one of them had suggested they should stretch their legs.',
          ],
        },
        {
          heading: 'An Art Gallery Appears',
          paragraphs: [
            'When we reached Tamarama, we didn’t follow the path all the way down to the beach.',
            'Instead, the route moved slightly inland and descended through the park.',
            'That’s when something unexpected started appearing.',
            'Sculptures.',
            'We hadn’t planned our walk around an art exhibition.',
            'We didn’t even know one was happening.',
            'By complete coincidence, we had arrived during Sculpture by the Sea.',
            'As we began climbing back toward the cliffs, installations appeared throughout the landscape.',
            'One would be positioned beside the path.',
            'Another would appear at an overlook.',
            'Others sat among the rocks with the Pacific behind them.',
            'A seated figure stared out toward the headland. An empty chair had a bird perched on it. Strange forms appeared against the rocks. Other pieces used the coastline itself almost as part of the installation.',
            'We had accidentally walked into an outdoor art gallery.',
            'And the gallery had a pretty ridiculous backdrop.',
          ],
        },
        {
          heading: 'Watch the Water',
          paragraphs: [
            'At Mackenzies Point, we stopped again.',
            'You could catch a glimpse of Bondi from there, but that wasn’t really what held our attention.',
            'The Pacific seemed enormous.',
            'Then someone spotted a blow out on the water.',
            'Whale.',
            'At first, most of us were just staring toward the general area where someone had seen it.',
            'Nothing.',
            'Ocean.',
            'More ocean.',
            'Then another blow appeared.',
            'Now everyone had it.',
            'For the next few minutes we stood at the lookout tracking the whale in the distance, waiting for each new appearance to tell us where to look next.',
            'We hadn’t planned that either.',
            'At this point, the walk seemed to be making up its own itinerary.',
          ],
        },
        {
          heading: 'Spring Around Squids Bay',
          paragraphs: [
            'As we continued toward Bondi, the beach disappeared from view again.',
            'The coastline folded around Squids Bay, and the trail dropped through a series of switchback-like steps.',
            'Along the path, spring flowers were in bloom.',
            'After miles of blue water, sandstone cliffs and beaches, the sudden color along the trail was another reminder that we’d arrived in Australia during spring.',
            'Bondi was close.',
            'We just couldn’t see it anymore.',
            'Then we climbed again.',
          ],
        },
        {
          heading: 'There It Is',
          paragraphs: [
            'Just before reaching the Bondi Icebergs, the coastline finally opened up.',
            'There it was.',
            'Bondi.',
            'Not the little glimpse we’d caught earlier from Mackenzies Point.',
            'The whole thing.',
            'The wide stretch of Bondi Beach curved out in front of us, with the town rising behind it.',
            'This was the destination I’d had in mind when I decided we should walk from Coogee in this direction.',
            'Coogee was where we started.',
            'Bondi was where we were going.',
            'And after all the bays, beaches, stairs, cliffs, sculptures and one unexpected whale, we’d made it.',
          ],
        },
        {
          heading: 'Surfers at Bondi',
          paragraphs: [
            'We walked past the Bondi Icebergs and around to the other side of the pools.',
            'Then we stopped.',
            'At the southern end of the beach, surfers were catching waves.',
            'So we stood there for a while and watched.',
            'That felt like the right place to end the walk.',
            'Only a few hours earlier, we’d been sitting on an airplane.',
            'Now the entire crew was standing above Bondi Beach watching surfers in the Pacific.',
            'We had managed to stay awake.',
            'We had definitely stretched our legs.',
            'Probably more than necessary.',
            'And somewhere nearby was lunch.',
          ],
        },
      ],
    }),
    reference('exploring-the-rocks', 20, 'Days 1, 2 & 4', 'We discovered The Rocks through an unplanned first-night pub stop, returned for a Day 2 pub crawl, and stopped there again after the Manly ferry outing.', {
      cardImage: cardImage('exploring-the-rocks-card', rooftopPubImage, 'The Reckon crew at a rooftop pub overlooking Sydney Harbour'),
    }),
    reference('manly-ferry', 40, 'Day 4', 'We wanted to see Sydney from the water. That was really the beginning of the plan. We’d already seen the Harbour Bridge and Opera House from our table at Quay. We’d walked around Circular Quay and The Rocks. But Sydney is a harbor city, and it seemed like we were missing something if we never actually got out onto the harbor. We had also heard that Manly was worth checking out. Laid-back. Good beach. Easy to reach by ferry. After finishing our conference sessions one afternoon, we had no dinner reservation and nowhere we particularly needed to be. So we decided to go see Manly.', {
      cardImage: cardImage('manly-ferry-card', manlyBeachImage, 'The Reckon crew together at Manly Beach'),
      storyTitle: 'Sydney From the Water',
      detailLabel: 'Read the Ferry to Manly Story',
      whyWeWent: 'To spend an afternoon away from the conference while experiencing one of Sydney’s essential journeys.',
      whatWeRemember: 'The skyline views made the transportation itself part of the adventure.',
      relatedBadgeIds: ['ferry-to-manly-beach'],
      featured: true,
      storySections: [
        {
          heading: 'A Metro Station for Boats',
          paragraphs: [
            'We walked down to Circular Quay.',
            'Phillip had already figured out the ferry schedules and routes, so he became our navigator and led us toward the right wharf for Manly.',
            'Circular Quay made more sense once I started thinking about it like a metro station.',
            'Except for boats.',
            'Different wharves served different routes. Ferries arrived, loaded passengers and headed back out across the harbor. People moved through the terminal with the same familiarity they’d have catching a train home from work.',
            'We followed Phillip.',
            'He got us onto the right one.',
          ],
        },
        {
          heading: 'Outside',
          paragraphs: [
            'Once aboard, we found seats outside toward the rear of the ferry.',
            'We hadn’t come for the interior accommodations.',
            'We wanted the view.',
            'As the ferry pulled away from Circular Quay, the Harbour Bridge seemed almost directly overhead.',
            'Then there was the Opera House.',
            'It was immediately to our right and felt unbelievably close.',
            'On our first night in Sydney, we’d sat at Quay looking across the water toward it through the restaurant’s floor-to-ceiling windows.',
            'That had been an incredible view.',
            'This was completely different.',
            'From the ferry, the Opera House felt close enough that you could almost reach out and touch it.',
            'We weren’t looking at Sydney Harbour anymore.',
            'We were in it.',
          ],
        },
        {
          heading: 'Watching Sydney Disappear',
          paragraphs: [
            'We stayed outside.',
            'As the ferry continued toward Manly, Sydney slowly began to recede behind us.',
            'The skyline became smaller.',
            'Then the landmarks we’d been watching since we arrived began disappearing too.',
            'The Harbour Bridge.',
            'The Opera House.',
            'Eventually central Sydney was gone.',
            'The character of the harbor changed with it.',
            'Instead of staring at the landmarks, I started noticing the houses.',
            'Large waterfront homes lined parts of the bays along the route, the kind that immediately make you wonder just how ridiculous Sydney waterfront real estate prices must be.',
            'Closer to Manly, there was a sailing club off to the right.',
            'Small skiffs moved around the water with kids learning to sail.',
            'It felt considerably removed from the city we’d left behind, even though we’d gotten there using what was essentially public transportation.',
          ],
        },
        {
          heading: 'From Harbor to Ocean',
          paragraphs: [
            'We stepped off the ferry at Manly Wharf.',
            'From there, The Corso carried us across toward Manly Beach.',
            'That was another thing that struck me about Manly.',
            'It was incredibly walkable.',
            'The Corso was lined with shops and restaurants, but it didn’t really feel like the kind of beach town I was accustomed to back home.',
            'It felt like an actual neighborhood and commercial district that happened to connect two completely different bodies of water.',
            'Behind us was Sydney Harbour.',
            'Ahead of us was the Pacific Ocean.',
            'A few minutes of walking connected the two.',
          ],
        },
        {
          heading: 'Just Another Weeknight',
          paragraphs: [
            'When we reached Manly Beach, we walked out toward the water and took in the ocean.',
            'Then we turned left and headed north along the coast.',
            'There wasn’t really an attraction we were trying to reach.',
            'We just walked.',
            'The street running alongside the beach was lined with trees. People were exercising. Others were playing volleyball. Surfers were out catching waves.',
            'People were running, walking and simply spending time outside.',
            'It was a weeknight.',
            'That’s what made it memorable.',
            'Nobody seemed to be performing “beach life” for visitors. This was simply what people were doing after work on an ordinary evening in Manly.',
            'We had heard the place was laid-back.',
            'Now we understood what people meant.',
          ],
        },
        {
          heading: 'One More Stop',
          paragraphs: [
            'Eventually we turned around and headed back toward the ferry.',
            'By then it was getting close to dusk, and food and beer seemed like a good idea before crossing the harbor again.',
            {
              segments: [
                {
                  text: '4 Pines',
                  to: {
                    name: 'reckon-way-entity',
                    params: {
                      gallerySlug: 'sxsw-sydney-2025',
                      sectionKey: 'dining',
                      entityId: '4-pines-manly',
                    },
                  },
                },
                { text: ' was almost directly across from the wharf.' },
              ],
            },
            'We went upstairs, ordered beers and waited for a table to open.',
            'The place had the familiar trappings of an American microbrewery combined with what felt like the soundtrack and energy of a stereotypical Australian beach bar.',
            'Men at Work included.',
            'We ate some bar food, had a few beers and watched daylight disappear.',
            'Then it was time to catch our ferry.',
          ],
        },
        {
          heading: 'Sydney After Dark',
          paragraphs: [
            'The ride back was even better.',
            'When we left Circular Quay earlier, Sydney had been right on top of us.',
            'Then we’d watched it disappear.',
            'Now the process happened in reverse.',
            'Except this time it was dark.',
            'At first there wasn’t much to see.',
            'Then, as we got closer, the lights of Sydney began appearing in the distance.',
            'The skyline took shape.',
            'The city grew larger.',
            'And eventually the familiar landmarks returned.',
            'The Opera House.',
            'The Harbour Bridge.',
            'Everything illuminated against the night.',
            'The closer we got, the more dramatic it became.',
            'We had taken the ferry because we wanted to see Sydney from the water.',
            'It turned out we got to see it twice.',
            'Once while watching the city disappear behind us in daylight.',
            'And again while watching it come back to life in the dark.',
            'Manly was worth the trip.',
            'But the ferry was the reason we went.',
            'And in the end, it may have been the best part.',
          ],
        },
      ],
    }),
    reference('featherdale-sydney-wildlife-park', 63, 'Day 6', 'After spending the first part of the day exploring the Blue Mountains and Scenic World, Tom loaded us back into the Anderson’s Tours van. Our next stop was Featherdale Sydney Wildlife Park. We had spent the morning experiencing Australia’s landscape. Now it was time to meet some of the locals.', {
      cardImage: cardImage('featherdale-card', featherdaleImage, 'The Reckon crew gathered around a koala at Featherdale Sydney Wildlife Park'),
      storyTitle: 'The Pigeons Were Hungrier',
      detailLabel: 'Read the Featherdale Story',
      detailSectionId: 'activities',
      detailEntityId: 'blue-mountains',
      detailStoryPageSlug: 'featherdale',
      whyWeWent: 'To pet a koala, feed kangaroos, and see more of Australia’s native animals up close.',
      storySections: [
        {
          heading: 'First Up, the Koala',
          paragraphs: [
            'One of the first experiences was also the one you probably expect when visiting an Australian wildlife park.',
            'A koala.',
            'The setup was pretty simple.',
            'The koala was positioned on a branch while we gathered around it for a photo. We couldn’t hold it, but we could gently place a hand on its fur.',
            'That was the first surprise.',
            'Koalas are amazingly soft.',
            'The second thing we learned was that the koala posing patiently for your picture has limits.',
            'The animals are rotated through the experience because eventually they get overstimulated and grouchy.',
            'Which is understandable.',
            'Koalas seem to have two primary interests.',
            'Eating.',
            'Sleeping.',
            'Having a steady stream of tourists interrupt your nap for photographs apparently isn’t high on the list.',
            'So after a while, one koala gets to clock out and another takes over.',
            'Not a bad system.',
          ],
        },
        {
          heading: 'Lunch Delivery',
          paragraphs: [
            'From there, we found the kangaroos and wallabies.',
            'Several sections of Featherdale were set up almost like a petting zoo, with the animals roaming freely around you.',
            'You could purchase cups of feed, pour some into your hand and let the kangaroos and wallabies eat directly from your palm.',
            'At least that was the theory.',
            'I had pictured something a little more active.',
            'Maybe kangaroos hopping over when they saw the food.',
            'Some did.',
            'Others had clearly been doing this longer than we had.',
            'They were stretched out on the ground and showed absolutely no interest in getting up simply because another tourist had purchased a cup of food.',
            'If you wanted to feed them, you could come to them.',
            'We would crouch down, put a handful of food directly in front of their mouths and let them eat while they continued lying there.',
            'They had successfully trained the tourists.',
          ],
        },
        {
          heading: 'The Real Competition',
          paragraphs: [
            'There was another animal considerably more interested in the food.',
            'The pigeons.',
            'They weren’t part of the attraction.',
            'Nobody had purchased food with the intention of feeding them.',
            'They didn’t care.',
            'While some of Australia’s most recognizable animals could barely be bothered to lift their heads for lunch, the pigeons were aggressively working the cleanup operation.',
            'Anything that hit the ground was immediately interesting.',
            'They were fast.',
            'They were persistent.',
            'And the kangaroo area wasn’t their only operation.',
          ],
        },
        {
          heading: 'The Wombat Had the Same Problem',
          paragraphs: [
            'When we found the wombats, we discovered they were dealing with the same competition.',
            'The wombats were trying to eat.',
            'The pigeons were trying to make sure there weren’t any leftovers.',
            'Apparently this wasn’t opportunistic behavior limited to tourists dropping kangaroo feed.',
            'The pigeons had a system.',
            'Find an animal with food.',
            'Wait nearby.',
            'Move in.',
            'They say all the animals in Australia can kill you.',
            'Apparently they’d met the pigeons.',
          ],
        },
        {
          heading: 'Those Are Dogs',
          paragraphs: [
            'Then there were the dingoes.',
            'Dingoes occupy a particular place in the American imagination of Australian wildlife.',
            'You hear about them your entire life as these distinctly Australian wild animals.',
            'Then you finally see one.',
            'It looks like a dog.',
            'The dingoes were pacing around their yard, and that was essentially my reaction.',
            'Those are dogs.',
            'I know they’re not.',
            'But standing there watching them walk around, it was difficult not to think that somebody’s dog had gotten loose inside the wildlife park.',
          ],
        },
        {
          heading: 'The Australian Checklist',
          paragraphs: [
            'By the time we finished walking around Featherdale, we’d checked off a pretty good collection of animals that we’d associated with Australia long before actually getting there.',
            'Koalas.',
            'Kangaroos.',
            'Wallabies.',
            'Wombats.',
            'Dingoes.',
            'And, apparently, pigeons.',
            'But Featherdale was memorable because we weren’t simply looking through glass or across distant enclosures.',
            'We’d gotten close.',
            'We’d felt how surprisingly soft a koala was.',
            'We’d fed kangaroos and wallabies directly from our hands.',
            'We’d watched wombats eat while pigeons waited for their opportunity.',
            'And we’d discovered that if a kangaroo doesn’t feel like getting up for lunch, it simply waits until lunch comes to it.',
            'After a full day of cliffs, valleys, waterfalls, rainforests and impossibly steep railways, Featherdale gave us something completely different.',
            'An introduction to Australian wildlife.',
            'Although based purely on effort, the pigeons still won.',
          ],
        },
      ],
    }),
    reference('blue-mountains', 61, 'Day 6', 'For most of the week, our experience of Sydney had been concentrated around the harbor, the beaches, the neighborhoods and the conference. Friday was different. We had booked a private Blue Mountains tour through Anderson’s Tours for the entire crew. It would be our last full day in Australia and our chance to get well outside the city we’d spent the week exploring. Our pickup was Friday morning at a hotel near ours. We got there on time. Sydney traffic had other ideas.', {
      cardImage: cardImage('blue-mountains-card', blueMountainsImage, 'The Reckon crew overlooking the Three Sisters in the Blue Mountains', 'center center', 'portrait'),
      storyTitle: 'Beyond Sydney',
      detailLabel: 'Read the Blue Mountains Story',
      whyWeWent: 'To experience another side of New South Wales beyond the conference and harbor.',
      whatWeRemember: 'The scale of the landscape reset the pace of the week before the trip home.',
      relatedBadgeIds: ['scenic-world-blue-mountains'],
      storySections: [
        {
          heading: 'Waiting for Tom',
          paragraphs: [
            'Our driver and guide, Tom, was running a little late.',
            'He called to let us know traffic had slowed him down and that he’d be there shortly.',
            'A little while later, an Anderson’s Tours van pulled up.',
            'Tom had arrived.',
            'We climbed aboard and started heading west.',
            'Almost immediately, the tour began.',
            'Not because we’d reached the Blue Mountains.',
            'Because Tom started explaining Sydney.',
          ],
        },
        {
          heading: 'Watching Sydney Grow',
          paragraphs: [
            'All week we’d experienced Sydney mostly from its center.',
            'Now we were driving through it.',
            'As we moved through the suburbs, Tom talked about the city’s growth and the enormous amount of development underway to accommodate even more people in the years ahead.',
            'He pointed out areas where that expansion was happening and talked about how Sydney was continuing to push outward.',
            'It gave us a completely different sense of scale.',
            'The Sydney we’d experienced all week around Circular Quay, Darling Harbour and the surrounding neighborhoods was only a small piece of a much larger metropolitan area.',
            'For a while, the suburbs seemed endless.',
            'Then we started climbing.',
            'And with almost every mile, there seemed to be fewer people.',
            'Fewer buildings.',
            'More trees.',
            'More open space.',
            'The massive city Tom had just spent the drive describing gradually faded away behind us.',
            'We were entering the Blue Mountains.',
          ],
        },
        {
          heading: 'Lincoln’s Rock',
          paragraphs: [
            'Tom’s first stop wasn’t one of the major tourist overlooks.',
            'He took us to Lincoln’s Rock.',
            'It was a little off the beaten path, with only a short walk from the parking area before we reached a huge exposed section of rock overlooking the valley.',
            'The view was spectacular.',
            'So was the wind.',
            'We could walk directly out onto the exposed rock, but the gusts made you much more conscious of where you were standing.',
            'You didn’t need to be anywhere near the cliff edge.',
            'Even ten or twenty feet back, I never felt quite as sure-footed as I wanted to be.',
            'There was nothing inherently dangerous about where we were standing.',
            'Your brain just kept reminding you what was beyond the edge.',
            'Then Tom’s hat demonstrated exactly why.',
          ],
        },
        {
          heading: 'Gone',
          paragraphs: [
            'Tom gathered everyone together for a group photo.',
            'A gust of wind caught his hat.',
            'Gone.',
            'It sailed straight over the cliff and disappeared from view.',
            'There wasn’t much time to react.',
            'The hat was simply there one second and somewhere down in the valley the next.',
            'Or so we thought.',
            'Before anyone could really process what had happened, a thermal updraft combined with another gust of wind caught the hat somewhere below us.',
            'Suddenly it came shooting back up above the cliff.',
            'For a moment it was airborne again.',
            'Then another gust carried it toward the low vegetation near the footpath we’d used to reach the overlook.',
            'Against fairly ridiculous odds, Tom got his hat back.',
            'It was probably the most effective wind-safety demonstration he could have given us.',
            'Nobody needed to be reminded to watch their footing after that.',
          ],
        },
        {
          heading: 'Echo Point',
          paragraphs: [
            'From Lincoln’s Rock, Tom took us to Echo Point.',
            'The experience was completely different.',
            'Lincoln’s Rock had felt exposed and relatively removed from everything.',
            'Echo Point was the iconic Blue Mountains viewpoint.',
            'Tom dropped us off and gave us time to explore on our own.',
            'We moved between the main lookouts and observation areas, taking in the enormous valley and finally getting our first clear look at the Three Sisters.',
            'It was one of those landscapes that doesn’t fit particularly well into a photograph.',
            'You can capture the rock formations.',
            'You can capture the cliffs.',
            'You can capture pieces of the valley.',
            'It’s much harder to capture the scale of everything together.',
            'So we wandered between the overlooks and took plenty of pictures anyway.',
          ],
        },
        {
          heading: 'Flat White Break',
          paragraphs: [
            'We didn’t turn Echo Point into another hike.',
            'Once we’d explored the main observation areas, we headed back toward the visitor center.',
            'There was a cafe inside.',
            'By Friday, the flat white had worked its way comfortably into the trip.',
            'So while Tom brought the van around, we grabbed flat whites and croissants and took a short break.',
            'Coffee.',
            'Croissant.',
            'Three Sisters outside.',
            'Not a bad Friday morning.',
            'Then Tom picked us up again.',
            'We had one more stop before Scenic World.',
          ],
        },
        {
          heading: 'Into the Woods',
          paragraphs: [
            'Tom took us for a short walk near Katoomba Cascades.',
            'Compared with standing on the exposed rock at Lincoln’s Rock or looking across the enormous valley from Echo Point, this part of the morning felt much smaller and quieter.',
            'The trail was an easy walk through the woods.',
            'We reached a small waterfall and continued along the path.',
            'Then, through an opening, we caught sight of something suspended across the valley.',
            'A cable car.',
            'Scenic World’s Skyway was moving high above the landscape in the distance.',
            'We stood there looking at it from the trail.',
            'We didn’t know it yet, but later that day we’d be inside that same car, looking back across the valley from the opposite perspective.',
          ],
        },
        {
          heading: 'Time to Go Down',
          paragraphs: [
            'That had been the rhythm of the morning.',
            'Tom would show us one version of the Blue Mountains, then take us somewhere that changed the perspective again.',
            'Lincoln’s Rock gave us the exposed cliffs, the wind and a hat that somehow returned from the valley.',
            'Echo Point gave us the enormous landscape and the Three Sisters.',
            'Katoomba Cascades took us into the woods and gave us our first distant look at the machinery hanging above the valley.',
            'Now Tom was taking us to Scenic World.',
            'Up to this point, we’d spent the morning looking across the Blue Mountains.',
            'Next, we were going down into them.',
          ],
        },
      ],
    }),
    reference('scenic-world', 62, 'Day 6', 'By the time we arrived at Scenic World, we’d already spent much of the morning looking into the Blue Mountains. We’d stood on the exposed rock at Lincoln’s Rock. We’d seen the Three Sisters from Echo Point. We’d walked through the woods to Katoomba Cascades, where an overlook gave us a glimpse of a cable car suspended across the valley in the distance. Now we were going to stop looking down into the valley. We were going into it.', {
      cardImage: cardImage('scenic-world-card', scenicWorldRailwayImage, 'The Reckon crew riding the Scenic Railway at Scenic World', 'center center', 'portrait'),
      storyTitle: 'Into the Valley',
      detailLabel: 'Read the Scenic World Story',
      detailSectionId: 'activities',
      detailEntityId: 'blue-mountains',
      detailStoryPageSlug: 'scenic-world',
      storySections: [
        {
          heading: 'The Steep Way Down',
          paragraphs: [
            'We started with the Scenic Railway.',
            'You can read that something is steep.',
            'You can look at it and understand that it’s steep.',
            'Then you sit down in the railway car.',
            'It felt unnaturally steep.',
            'Physics-defying steep.',
            'The strange part was that the train itself wasn’t moving particularly fast. This wasn’t a roller coaster pretending to be a railway.',
            'The descent was controlled.',
            'It was the geometry that made everything feel wrong.',
            'The angle of the car, the seat and your body didn’t seem to agree with one another. Your brain knows what sitting in a train is supposed to feel like.',
            'This wasn’t it.',
            'We slowly descended into the valley, eventually trading the expansive views from above for the forest below.',
          ],
        },
        {
          heading: 'On the Forest Floor',
          paragraphs: [
            'At the bottom, we followed the boardwalk through the rainforest.',
            'Tom stayed with us, explaining the plants and geography as we walked.',
            'All morning he’d been doing more than simply driving us from one attraction to another. He’d been adding context to what we were seeing, and down in the rainforest that became even more valuable.',
            'He also talked about Aboriginal stories associated with the Blue Mountains, including stories of the Three Sisters and the lyrebird.',
            'Then we heard one.',
          ],
        },
        {
          heading: 'Listen',
          paragraphs: [
            'I’m not sure we would have known what we were hearing without Tom.',
            'He did.',
            'Instead of continuing down the boardwalk, we stopped and listened.',
            'Tom helped us figure out where the sound was coming from and started looking through the woods.',
            'Eventually we found the bird on the forest floor.',
            'Then another.',
            'And another.',
            'By the time we’d figured out what we were looking for, there were four lyrebirds moving around the same general area.',
            'It was another example of why having Tom with us mattered.',
            'Without him, we might have heard an unusual bird somewhere in the trees, wondered what it was and continued walking.',
            'Instead, we stopped.',
            'We listened.',
            'We tracked it down.',
            'Then we realized we were surrounded by them.',
          ],
        },
        {
          heading: 'The Other Blue Mountains',
          paragraphs: [
            'We continued along the boardwalk and through the old mining area.',
            'That was another shift in perspective.',
            'From above, the Blue Mountains had seemed almost entirely untouched. Huge valleys covered in forest stretched toward the horizon.',
            'Down on the valley floor, there were reminders that people had worked in this landscape too.',
            'The boardwalk eventually brought us back toward our route out.',
            'The plan had been simple.',
            'Railway down.',
            'Cableway back up.',
            'There was one problem.',
            'The Cableway was closed for maintenance.',
            'Apparently there was only one way out.',
            'Back to the train.',
          ],
        },
        {
          heading: 'Now Do It Backwards',
          paragraphs: [
            'We’d already experienced the strange geometry of the Scenic Railway once.',
            'Now we got to experience it in reverse.',
            'Literally.',
            'The train began pulling us backward up the same extremely steep hillside we’d descended earlier.',
            'It wasn’t scary.',
            'It was just a very strange sensation.',
            'Sitting in a railway car while being pulled backward up a grade that steep isn’t something your body has much prior experience with.',
            'Eventually we reached the top.',
            'At least this time we knew exactly how steep it was.',
          ],
        },
        {
          heading: 'Across the Valley',
          paragraphs: [
            'There was one more ride.',
            'The Scenic Skyway.',
            'Earlier, during our walk near Katoomba Cascades, we’d seen it crossing the valley from a distance.',
            'Now we stepped inside.',
            'The Skyway carried us out over the valley we’d spent the morning exploring.',
            'From the car, the individual pieces of the day started fitting together.',
            'We could see the Three Sisters.',
            'Katoomba Falls.',
            'The cliffs.',
            'The forest below.',
            'The area we’d just descended into on the railway and explored on foot was now beneath us.',
            'All morning we’d been changing our relationship with the same landscape.',
            'First looking across it.',
            'Then looking down into it.',
            'Then standing on the forest floor inside it.',
            'Now we were suspended above it.',
          ],
        },
        {
          heading: 'One Valley, Four Perspectives',
          paragraphs: [
            'The Skyway carried us to the other side, and that was the end of our Scenic World visit.',
            'What I remember most isn’t any single ride.',
            'It’s how different the Blue Mountains looked depending on where we were standing.',
            'At Echo Point, they were enormous.',
            'From the Scenic Railway, they were impossibly steep.',
            'On the rainforest floor, they became intimate enough that we could stop and listen for a bird moving through the vegetation.',
            'From the Skyway, everything opened up again.',
            'The Three Sisters.',
            'The waterfall.',
            'The cliffs.',
            'The rainforest.',
            'All part of the same landscape we’d spent the day moving through.',
            'Scenic World didn’t just give us another view of the Blue Mountains.',
            'It kept changing the view.',
          ],
        },
      ],
    }),
  ],
  restaurants: [
    reference('bondi-trattoria', 2, 'Day 1', 'I wish I could say Bondi Trattoria had been carefully researched. It wasn’t. We had just finished walking from Coogee to Bondi after flying halfway around the world. We were hungry. Bondi Trattoria was right there. Sometimes that’s enough.', {
      cardImage: cardImage('bondi-trattoria-card', bondiTrattoriaImage, 'The Reckon crew gathered for lunch at Bondi Trattoria'),
      storyTitle: 'The First Beer in Australia',
      detailLabel: 'Read the Bondi Trattoria Story',
      whatWeRemember: 'It was the perfect place to settle in, soak up the atmosphere, and officially begin the week.',
      storySections: [
        {
          heading: 'Straight From the Airport',
          paragraphs: [
            'Our first morning in Sydney had gotten a little more ambitious than originally intended.',
            'We had landed that morning, dropped our bags at the hotel and discovered that not all of our rooms were ready.',
            'After spending an inordinate amount of time sitting on airplanes, I thought getting outside and stretching our legs would be better than sitting around the hotel waiting.',
            'So the entire crew took a taxi to Coogee.',
            'Then we walked to Bondi.',
            'What began as an attempt to get everyone moving had turned into miles of coastline, beaches, cliffs, hills and more stairs than were probably necessary immediately after an international flight.',
            'It had also been spectacular.',
            'We’d walked through Gordon’s Bay and Clovelly, past Waverley Cemetery and down to Bronte. We’d accidentally found ourselves in the middle of Sculpture by the Sea. We’d stood at Mackenzies Point and tracked a whale by watching for its blow in the distance.',
            'Eventually Bondi Beach opened up in front of us.',
            'We walked past the Icebergs and stopped for a while to watch surfers catching waves at the southern end of the beach.',
            'Then our priorities changed.',
            'We needed lunch.',
          ],
        },
        {
          heading: 'The Closest Restaurant',
          paragraphs: [
            'There wasn’t much science behind choosing Bondi Trattoria.',
            'It was close.',
            'Really close.',
            'After the morning we’d just had, nobody was arguing that we needed to walk another mile in search of a restaurant with better online reviews.',
            'Bondi Trattoria was near where we’d come off the coastal path and just across from the beach.',
            'Decision made.',
            'We went inside.',
          ],
        },
        {
          heading: 'Inside, Sort Of',
          paragraphs: [
            'Our table was just inside the restaurant on the right.',
            'But “inside” is probably overstating it.',
            'The large doors and windows beside us were completely open, leaving the restaurant exposed to the street and the air coming off Bondi.',
            'We had spent the entire morning outside, and lunch felt like an extension of that.',
            'People moved past outside.',
            'The beach was close.',
            'Sydney was happening around us.',
            'For the first time since getting off the plane, all seven of us were sitting down together with nowhere we needed to walk.',
            'That felt pretty good.',
          ],
        },
        {
          heading: 'The First Beer',
          paragraphs: [
            'Then came another important milestone.',
            'Our first beers in Australia.',
            'After flying across the Pacific and walking from Coogee to Bondi, that first cold beer tasted particularly well earned.',
            'There was just one problem.',
            'No Fosters.',
            'We had arrived in Australia carrying the American assumption that Fosters would be everywhere.',
            'Apparently not.',
            'At this point it was just a mildly amusing observation.',
            'We had no idea that finding a Fosters was about to become an ongoing challenge for the rest of the trip.',
            'So we ordered something else.',
            'There would be plenty of Australian beer ahead of us.',
          ],
        },
        {
          heading: 'Pizza, Pasta and Finally Sitting Down',
          paragraphs: [
            'Lunch wasn’t complicated.',
            'Pizza.',
            'Pasta.',
            'Beer.',
            'Exactly what we needed.',
            'Some ordered pizza, others went with pasta, and food gradually filled the table.',
            'There wasn’t a tasting menu to analyze or a famous steak we needed to compare with another famous steak.',
            'Nobody needed the meal explained.',
            'We just ate.',
            'Looking around the table, everyone was tired.',
            'Of course we were.',
            'Our bodies had traveled halfway around the planet and weren’t entirely sure what time it was supposed to be.',
            'But there was something else there too.',
            'Excitement.',
            'We were finally in Sydney.',
            'The flight was behind us.',
            'The entire week was still ahead.',
          ],
        },
        {
          heading: 'A Pretty Good First Day',
          paragraphs: [
            'Bondi Trattoria wasn’t one of the restaurants that brought us to Sydney.',
            'It wasn’t a difficult reservation.',
            'We hadn’t crossed the city to find it.',
            'We walked in because it happened to be almost exactly where we were standing when seven hungry people collectively decided they’d walked far enough.',
            'And that made it perfect.',
            'We sat beside the open windows eating pizza and pasta with our first Australian beers on the table and Bondi Beach just outside.',
            'Tired enough to know we’d traveled a very long way.',
            'Excited enough that nobody particularly cared.',
            'Not bad for a group that had landed in Australia only a few hours earlier.',
            'Still no Fosters.',
          ],
        },
      ],
    }),
    reference('quay-sydney', 20, 'Day 1', 'Our first dinner in Sydney paired Peter Gilmore’s tasting menu with a harbor view that immediately put us somewhere unmistakably Sydney.', {
      cardImage: cardImage('quay-card', quayDinnerImage, 'The Reckon crew dining at Quay with the Sydney Opera House behind them'),
      storyTitle: 'Our First Table in Sydney',
      detailLabel: 'Read the Quay Story',
      whatHappened: 'Amazing views of Sydney Harbour were matched by equally impressive food presentation.',
      whatWeRemember: 'An amazing way to start the week together, 14 hours ahead of home.',
      featured: true,
      storySections: [
        {
          heading: 'Starting Without Brandon',
          paragraphs: [
            'Quay sits inside the International Passenger Terminal at The Rocks, directly on Sydney Harbour.',
            'The six of us were supposed to meet at the hotel and take a cab over together. Five of us made it.',
            'We couldn’t get Brandon L. to answer.',
            'After the flight to Sydney and the inevitable confusion your body experiences when you’ve crossed that many time zones, we had a pretty good theory.',
            'He had probably fallen asleep.',
            'So Brandon A., Phillip, Matt, Ben, and I piled into a cab and headed to Quay without him.',
            'Fortunately, Brandon L. eventually resurfaced and caught up with us before the first course was served.',
            'The Reckon Crew was officially back to full strength.',
          ],
        },
        {
          heading: 'That View',
          paragraphs: [
            'Walking into Quay immediately explained why I had chosen it.',
            'The restaurant is wrapped in floor-to-ceiling glass. When you step inside, the Sydney Harbour Bridge feels almost directly overhead to your left.',
            'Then they led us through the restaurant to our table.',
            'We were seated beside the windows looking directly across the harbor at the Sydney Opera House.',
            'It was still daylight when we arrived, which turned out to be perfect.',
            'Over the course of dinner, Sydney slowly transitioned from afternoon into dusk and then darkness. The Opera House changed with the light outside, and dusk was probably when the view was at its best.',
            'We kept expecting the Opera House to suddenly light up once darkness arrived.',
            'It never really did.',
            'Apparently one of the most famous buildings in the world doesn’t feel the need to show off at night.',
          ],
        },
        {
          heading: 'Whatever They Put in Front of Us',
          paragraphs: [
            'We chose the tasting menu.',
            'By this point, everyone has become pretty adventurous when we travel together. There is an understanding that at some point I’m probably going to take them somewhere where something they weren’t expecting is going to appear on the plate.',
            'Nobody seems particularly concerned about it anymore.',
            'Quay was a perfect example.',
            'Peter Gilmore’s menu leaned heavily into Australian ingredients, and throughout the evening things arrived that many of us either hadn’t eaten before or wouldn’t have known how to identify without someone explaining them.',
            'That has become part of the fun.',
            'I don’t remember the name of every course now, and I don’t want to pretend that I do. What I remember much more clearly is how they arrived.',
            'Each plate felt composed rather than simply plated. There were tiny flowers and petals arranged almost individually. Seafood appeared over crushed ice. Seaweed became part of the landscape of a dish. Mushrooms were layered over Wagyu. One course arrived looking almost more like sculpture than food.',
            'Even the bowls and plates seemed designed around whatever had been placed inside them.',
            'It became one of those meals where every new course caused everyone to stop talking for a second and look at what had just landed on the table.',
            'Then we ate it.',
          ],
        },
        {
          heading: 'Australia on the Plate',
          paragraphs: [
            'What made Quay especially appropriate for our first night wasn’t simply that it was fine dining.',
            'It was Australian fine dining.',
            'The tasting menu introduced us to ingredients and combinations rooted in where we had just arrived. There was Western Australian marron, Australian maitake mushrooms, Wagyu, unusual seafood preparations, and ingredients that required a little explanation when they reached the table.',
            'We weren’t trying to memorize them.',
            'We were experiencing them.',
            'That’s an important distinction with these dinners.',
            'The point isn’t to become experts in Australian ingredients over the course of a tasting menu. It’s to sit down together somewhere completely new and be willing to try whatever comes next.',
            'By now, the crew knows the deal.',
            'You may not know what I’m taking you to.',
            'You may not recognize everything on the plate.',
            'Try it anyway.',
          ],
        },
        {
          heading: 'White Coral',
          paragraphs: [
            'The meal eventually finished with one of Peter Gilmore’s signature creations, White Coral.',
            'It looked exactly like the name suggests: a delicate white sphere with a porous, almost coral-like exterior.',
            'It was another course where the presentation made you hesitate before breaking into it.',
            'That seemed like an appropriate ending to the meal.',
            'We had spent the evening watching Sydney Harbour change outside the windows while a succession of things we hadn’t expected appeared on the table in front of us.',
            'Outside, the Opera House had faded into the darkness rather than dramatically lighting up.',
            'Inside, the final course was sitting in front of us looking like a piece of coral.',
            'Our first dinner in Australia had done exactly what I hoped it would.',
            'It had put us somewhere unmistakably Sydney.',
          ],
        },
        {
          heading: 'One We Can’t Go Back To',
          paragraphs: [
            'We didn’t know it that night, but our timing turned out to matter.',
            'Just a few months after our dinner, Quay closed its doors for good. Its final service was February 14, 2026, bringing more than two decades of Peter Gilmore’s cooking at the restaurant to an end.',
            'That means there won’t be a return visit on some future Reckon trip to Sydney.',
            'I’m glad we didn’t wait.',
            'There are restaurants we visit and immediately start talking about coming back to someday. Quay became something different. We got one night there, sitting beside the harbor as daylight disappeared over the Opera House, passing unfamiliar plates around a table with the crew.',
            'Sometimes you don’t know you’re having the only version of an experience you’ll ever get until it’s already behind you.',
          ],
        },
        {
          heading: 'Into The Rocks',
          paragraphs: [
            'Fortunately, our night wasn’t over yet.',
            'When dinner ended, we stepped out of Quay and started walking through The Rocks.',
            'The neighborhood felt completely different from the newer parts of Sydney we had seen so far. Older buildings, narrow streets, pubs and restaurants tucked into historic storefronts. It reminded me a little of Europe mixed with some of the older cities back home.',
            'We hadn’t planned a pub stop.',
            'Then we walked past the Fortune of War.',
            'And apparently five of us weren’t quite ready for our first night in Sydney to end.',
          ],
        },
      ],
    }),
    reference('fortune-of-war', 21, 'Day 1', 'Our first night in Sydney was supposed to end with the walk back from Quay. Instead, an unplanned stop at the Fortune of War Hotel gave us live music, a few pints, and our first proper look at The Rocks.', {
      storyTitle: 'A Few Pints in the Wrong Time Zone',
      detailLabel: 'Read the Fortune of War Story',
      whatHappened: 'We heard live music spilling out onto the street and learned it claimed to be Sydney’s “oldest” pub. How could we not stop?',
      whatWeRemember: 'An unplanned stop became the perfect continuation of the evening after Quay.',
      storySections: [
        {
          heading: 'One More Stop',
          paragraphs: [
            'After dinner at Quay, we started walking back to the hotel. It had already been a full first night in Sydney, and our bodies had no idea what time zone they were in.',
            'Ben made the responsible decision. He headed back to the hotel to try to sleep.',
            'The rest of us kept going.',
            'That left me, Brandon A., Brandon L., Phillip, and Matt walking through The Rocks when we came across what we understood to be the “oldest pub” in Sydney. Whether we needed another drink was beside the point. We were in a new city, it was our first night, and there was live music coming from inside.',
            'We stopped.',
          ],
        },
        {
          heading: 'Holding Up the Wall',
          paragraphs: [
            'There was no grand plan once we entered the Fortune of War Hotel. We found a spot along the wall near the far side of the bar and posted up for the evening.',
            'A small ledge ran along the wall, just wide enough to hold our drinks. It became our table, our home base, and probably the only thing keeping five jet-lagged travelers organized.',
            'We ordered a few pints and settled in.',
            'The live music gave us something to listen to without requiring much effort. The crowd gave us something to watch. Conversation came and went, but nobody needed the night to become anything more than it was. We could lean against the wall, balance our beers on the ledge, and let Sydney happen around us.',
            'There is a particular kind of exhaustion that arrives after crossing enough time zones. You are tired, but your body cannot explain what kind of tired. You know you should sleep, but the local clock and the atmosphere around you make a convincing argument for staying out.',
            'So we stayed.',
          ],
        },
        {
          heading: 'The First Look at The Rocks',
          paragraphs: [
            'The walk back afterward was uneventful, which was probably for the best. The more lasting impression came from the neighborhood itself.',
            'The Rocks felt older than the parts of Sydney we had seen so far. It had a historic quality, with an atmosphere that seemed to sit somewhere between a European neighborhood and an older American city. Pubs and restaurants were tucked throughout the area, making it feel less like a district we were passing through and more like a place that invited wandering.',
            'Even through the fog of travel and a few pints, we could tell that this was somewhere we would need to explore again later in the week.',
            'On Day 2, we did exactly that, pub crawling through and around The Rocks and getting a much broader look at the neighborhood.',
            'We passed through again on the way back from our ferry ride to Manly, stopping once more before continuing to the hotel.',
            'That realization became the real value of the stop. The Fortune of War Hotel was not a destination we had carefully researched or built into the itinerary. It was a place we noticed while walking back from somewhere else. We went inside because there was music, because we were not ready to surrender the first night, and because an old Sydney pub seemed like a better place to negotiate with jet lag than a hotel room.',
            'Ben may have made the sensible choice.',
            'The rest of us found a ledge, ordered another round, and began getting acquainted with Sydney.',
          ],
        },
      ],
    }),
    reference('glenmore-hotel', 22, 'Day 2', 'The Glenmore Hotel was one of the stops on a Sydney pub crawl we had essentially invented for ourselves. There wasn’t an organized tour or carefully researched itinerary. Brandon L. put his internet search skills to work, found a collection of pubs that looked worth visiting, and we started making our way through them. Everyone who made the trip was along for the night. At the Glenmore, the beer wasn’t what we would remember. The view was.', {
      cardImage: cardImage('glenmore-card', rooftopPubImage, 'The Reckon crew at the Glenmore rooftop with the Sydney Opera House behind them'),
      storyTitle: 'The View at the Top',
      detailLabel: 'Read the Glenmore Story',
      storySections: [
        {
          heading: 'Where Is Everybody?',
          paragraphs: [
            'We walked into the Glenmore at street level on a Monday night and found a bar that was virtually empty.',
            'For a place that had apparently earned a spot on Brandon’s list, it wasn’t exactly an encouraging first impression.',
            'There were a few possibilities. It was Monday. We were relatively early. Or Brandon’s internet research had finally failed us.',
            'Then we found the stairs.',
            'Getting to the rooftop meant climbing a tight, narrow, winding staircase through the building.',
            'As we worked our way upward, it became increasingly obvious that the empty downstairs bar wasn’t telling us the whole story.',
            'Everyone was upstairs.',
          ],
        },
        {
          heading: 'The Rooftop',
          paragraphs: [
            'The rooftop wasn’t packed, but compared with the nearly deserted bar downstairs, it felt like an entirely different place.',
            'We had no trouble finding a table near the railing, which turned out to be exactly where we wanted to be.',
            'Across the rooftops of The Rocks and Circular Quay was the Sydney Opera House.',
            'By then it was fully dark, and from our seats along the rail we had an unobstructed view across the harbor.',
            'That was the Glenmore.',
            'There are places where you remember the beer. Places where you remember the food. Places where some ridiculous conversation becomes the story everyone tells later.',
            'At the Glenmore, we remember where we were sitting and what we could see.',
          ],
        },
        {
          heading: 'Still No Fosters',
          paragraphs: [
            'It was a pub crawl, so most of us ordered a pint.',
            'Something local.',
            'By this point we had also discovered something mildly surprising about drinking our way around Sydney.',
            'We still couldn’t find Fosters.',
            'For a beer that had spent decades being advertised back home as practically synonymous with Australia, actual Australians seemed remarkably uninterested in helping us locate one.',
            'So we continued doing what we had been doing.',
            'We drank the local beer instead.',
            'It wasn’t much of a sacrifice.',
          ],
        },
        {
          heading: 'One Pint, One View',
          paragraphs: [
            'We weren’t at the Glenmore for an elaborate night out. It was one stop among several, part of a route Brandon L. had assembled and we were discovering together as we went.',
            'That is probably why the simplicity of it worked.',
            'Climb the narrow stairs.',
            'Find a table along the rail.',
            'Order a pint.',
            'Look across Sydney Harbour at one of the most recognizable buildings in the world.',
            'Then finish your beer and head for the next pub.',
            'Sometimes that’s enough to earn a place a spot in the story.',
          ],
        },
      ],
    }),
    reference('australian-heritage-hotel', 23, 'Day 2', 'There are some foods you seek out because they’re supposed to be exceptional. And then there are foods you seek out because someone tells you that you can eat a kangaroo and an emu on the same pizza. The Australian Heritage Hotel fell firmly into the second category. It was another stop on the Monday-night pub crawl Brandon L. had assembled for us, but unlike some of the pubs on the route, we had a very specific reason for going. We wanted the Coat of Arms pizza.', {
      cardImage: cardImage('heritage-hotel-card', heritageHotelImage, 'The Reckon crew sharing Coat of Arms Pizza at the Australian Heritage Hotel'),
      storyTitle: 'Eating the Coat of Arms',
      detailLabel: 'Read the Australian Heritage Hotel Story',
      whatWeRemember: 'Reviews were mixed, but nobody regretted giving it a try.',
      storySections: [
        {
          heading: 'Eating the Coat of Arms',
          paragraphs: [
            'Australia’s coat of arms features two native animals: a kangaroo and an emu.',
            'The Australian Heritage Hotel had decided the logical thing to do with that information was put both of them on a pizza.',
            'Naturally, we needed to try it.',
            'We walked into what felt like a traditional Australian pub, found the bar, ordered another round of pints and put in an order for the Coat of Arms pizza.',
            'Kangaroo and emu.',
            'Since apparently two Australian animals weren’t enough for one evening, we ordered another pizza with crocodile as well.',
            'Then we took our drinks outside and grabbed a table on the sidewalk right beside the pub entrance.',
          ],
        },
        {
          heading: 'Everybody Gets a Slice',
          paragraphs: [
            'The pizzas weren’t meant to be dinner for any one person.',
            'We ordered them for the table so everyone could grab a slice and try something they probably hadn’t expected to eat when we started planning a trip to Sydney.',
            'There wasn’t much hesitation.',
            'By this point, that’s become part of traveling together. If there is something unusual and distinctly local to try, chances are it’s eventually going to end up in the middle of our table.',
            'Everybody tried everything.',
            'The funny part was how normal it all tasted.',
            '“Kangaroo, emu and crocodile” sounds considerably more adventurous than the actual experience of eating them on a pizza. Once chopped, seasoned and scattered among familiar pizza ingredients, they weren’t radically different from toppings we would recognize back home.',
            'Think somewhere in the general neighborhood of sausage, pepperoni or ground beef.',
            'The names were considerably more exotic than the flavors.',
          ],
        },
        {
          heading: 'Another Pint',
          paragraphs: [
            'That was really all the Australian Heritage Hotel needed to be.',
            'We weren’t there for a long dinner. We weren’t dissecting the differences between kangaroo and emu or pretending we’d suddenly developed sophisticated opinions about crocodile.',
            'We were on a pub crawl.',
            'So we sat outside on the sidewalk, drank another pint, passed slices around the table and watched everyone have their first bite of something new.',
            'Inside was the traditional pub Brandon’s search had promised us.',
            'Outside was the Reckon Crew eating Australia’s coat of arms.',
            'Not a bad stop for a Monday night.',
          ],
        },
        {
          heading: 'The Kind of Stop You Remember',
          paragraphs: [
            'The Australian Heritage Hotel also captures something that has become increasingly normal on these trips.',
            'Nobody needed convincing.',
            'There was a time when putting kangaroo, emu and crocodile in front of a group might have required a little salesmanship. By Sydney, everyone understood how this worked.',
            'We’re somewhere new.',
            'There’s something here we can’t easily try at home.',
            'Order enough for the table.',
            'Everybody gets a piece.',
            'Then finish your pint and see where Brandon L. is taking us next.',
          ],
        },
      ],
    }),
    reference('harts-pub', 24, 'Day 2', 'Every pub crawl needs a first pint. Ours started at Hart’s Pub. Brandon L. had used his internet search skills to assemble a route through some of Sydney’s pubs, and on Monday night the entire crew set out to see how well he’d done. Hart’s was stop number one. It also turned out to be where one of the night’s running jokes began.', {
      storyTitle: 'Where the Crawl Began',
      detailLabel: 'Read the Hart’s Pub Story',
      storySections: [
        {
          heading: 'A Neighborhood Pub',
          paragraphs: [
            'Hart’s immediately felt different from somewhere chosen because it had a famous view or a particular thing we needed to eat.',
            'It felt like a neighborhood pub.',
            'The downstairs wasn’t particularly large, and most of the tables were already occupied by people who looked like locals. The bar itself seemed designed primarily for ordering. You walked up, figured out what you wanted, got your beer and moved along so the next person could do the same.',
            'There wasn’t really room for seven of us to linger there anyway.',
            'So, pints in hand, we went looking for somewhere else to sit.',
          ],
        },
        {
          heading: 'Upstairs',
          paragraphs: [
            'We found an outdoor area upstairs.',
            'Unlike downstairs, we had it completely to ourselves.',
            'There were large round tables with tabletops fastened to old wooden barrels, exactly the sort of furniture that makes perfect sense in a pub.',
            'We claimed one and settled in.',
            'Downstairs, the neighborhood pub carried on without us. Upstairs, seven guys who had traveled from Virginia to Sydney sat around an old barrel with their first pints of a pub crawl Brandon had assembled from internet searches.',
            'It was a pretty good start.',
          ],
        },
        {
          heading: 'Where’s the Fosters?',
          paragraphs: [
            'Somewhere around that first round, the question came up.',
            'Do they have Fosters?',
            'Growing up in the United States, we’d been taught through years of advertising that Fosters was essentially Australia in a blue can.',
            'So naturally, now that we were actually drinking in an Australian pub, somebody wanted one.',
            'No luck.',
            'That was mildly amusing at Hart’s.',
            'It became considerably funnier as the trip continued.',
            'From that point forward, Fosters became something of an unofficial scavenger hunt. We kept encountering local beers, Australian breweries and plenty of things we’d never seen back home.',
            'But the supposedly obvious Australian beer remained strangely elusive.',
            'So we drank something local instead.',
          ],
        },
        {
          heading: 'Make It Two',
          paragraphs: [
            'Hart’s was supposed to be the beginning of a pub crawl, which generally suggests a certain amount of forward progress.',
            'We had two pints.',
            'There was no reason to rush.',
            'We had the upstairs to ourselves, a big barrel table to gather around and nowhere particularly important we needed to be except the next pub on Brandon’s list.',
            'That became part of the rhythm of the night.',
            'The route gave us direction, but there wasn’t a schedule attached to it. If we liked where we were, we stayed for another beer.',
            'At Hart’s, we did.',
            'Eventually we finished the second round, headed back downstairs and stepped out into The Rocks.',
            'Next stop: the Australian Heritage Hotel.',
            'There were kangaroo, emu and crocodile pizzas waiting for us.',
            'We just didn’t know yet that the Fosters search was going to be considerably more difficult.',
          ],
        },
      ],
    }),
    reference('nine-dragons', 30, 'Day 3', 'For most of the crew, lunch was their first dim sum experience.', {
      whatHappened: 'Kevin ordered the classics he remembered from family trips to Philadelphia’s Chinatown and watched the team discover them for the first time.',
      whatWeRemember: 'Familiar dishes became a new shared first.',
      storyTitle: 'My Turn to Order',
      detailLabel: 'Read the Nine Dragons Story',
      storySections: [
        {
          heading: 'My Turn to Order',
          paragraphs: [
            'After a morning session at SXSW Sydney, I already knew what I wanted for lunch.',
            'Dim sum.',
            'A few mornings earlier, I’d been wandering around near the hotel looking for coffee when I realized just how close we were to Chinatown.',
            'A block or two.',
            'That settled it.',
            'At some point during the week, I was going to have dim sum.',
            'Today was the day.',
          ],
        },
        {
          heading: 'Follow Me',
          paragraphs: [
            'I’d Googled a few restaurants ahead of time, but I hadn’t picked one.',
            'The plan was simply to walk into Chinatown, look around and find somewhere that felt right.',
            'We weren’t looking for a reservation or some highly curated dining experience.',
            'I knew what I wanted.',
            'I just needed to find the right room to eat it in.',
            'Eventually we found Nine Dragons.',
            'The restaurant was upstairs and wasn’t particularly busy when we arrived, but the reviews were solid.',
            'More importantly, it looked right.',
            'There was something immediately familiar about it.',
          ],
        },
        {
          heading: 'Sundays in Chinatown',
          paragraphs: [
            'Walking into Nine Dragons brought back memories I hadn’t necessarily expected to find on the other side of the world.',
            'Growing up, Sundays sometimes meant going into Philadelphia’s Chinatown with my family for dim sum.',
            'This felt familiar.',
            'Not because Sydney’s Chinatown looked exactly like Philadelphia’s or because Nine Dragons was somehow recreating a specific restaurant from my childhood.',
            'It was the experience.',
            'Walking upstairs into a traditional Chinatown restaurant.',
            'Sitting around a table.',
            'Ordering more dishes than any one person could possibly eat.',
            'Sharing everything.',
            'For most of the Reckon crew, what came next was going to be completely new.',
            'For me, it felt a little like going back.',
          ],
        },
        {
          heading: 'Wait, You’ve Never Had Dim Sum?',
          paragraphs: [
            'Once we sat down, I realized something.',
            'Most of the crew had never had dim sum before.',
            'Five first-timers.',
            'That changed the ordering process considerably.',
            'There would be no studying the menu and everyone selecting their own entrée.',
            'I was ordering.',
            'For everyone.',
            'I started with many of my own go-tos and mixed in what I considered a reasonable dim sum starter kit.',
            'Har gow.',
            'Shrimp dumplings.',
            'Siu mai.',
            'Pork dumplings.',
            'Steamed pork buns.',
            'Spare ribs with black bean sauce.',
            'Stuffed eggplant.',
            'Salt and pepper squid.',
            'Then there were the chicken feet.',
          ],
        },
        {
          heading: 'No Takers',
          paragraphs: [
            'Chicken feet were one of my orders.',
            'Apparently they were my order.',
            'I offered.',
            'Nobody seemed particularly interested in expanding their culinary horizons quite that far.',
            'I think I ate the entire order myself.',
            'Their loss.',
            'Fortunately, there was plenty of other food coming.',
          ],
        },
        {
          heading: 'Chopsticks Required',
          paragraphs: [
            'The dishes kept arriving and disappearing almost as quickly as they hit the table.',
            'That was probably the best review Nine Dragons could have gotten.',
            'The food went fast.',
            'It probably would have gone faster if I’d let everyone use forks.',
            'I didn’t.',
            'If we were going to introduce five people to dim sum, we were going to do this properly.',
            'Chopsticks.',
            'Some were more proficient than others.',
            'That was part of the experience.',
            'Eventually everyone figured out some combination of technique, determination and hunger that successfully moved food from the table to their mouths.',
          ],
        },
        {
          heading: 'Just in Case',
          paragraphs: [
            'I had also ordered some insurance.',
            'Beef chow fun.',
            'Pan-fried seafood noodles.',
            'Two larger noodle dishes to make sure that regardless of how adventurous everyone decided to be with everything else arriving at the table, nobody was leaving Chinatown hungry.',
            'It turned out not to be much of a concern.',
            'The food kept disappearing.',
            'Har gow.',
            'Siu mai.',
            'Pork buns.',
            'Ribs.',
            'Eggplant.',
            'Squid.',
            'Noodles.',
            'Everything except my apparently controversial chicken feet found plenty of willing participants.',
          ],
        },
        {
          heading: 'Shared Firsts',
          paragraphs: [
            'I think everyone liked it.',
            'At least they said they did.',
            'Maybe they were just being nice.',
            'But the empty plates provide some supporting evidence.',
            'Whether any of them will independently walk into a dim sum restaurant, refuse a fork and order chicken feet remains to be seen.',
            'For me, the meal meant something a little different.',
            'One of the things I like most about traveling with the crew is experiencing something for the first time together.',
            'But every shared experience doesn’t have to be new for everyone.',
            'Sometimes it’s just as much fun introducing people to something you’ve known your entire life.',
            'For five of them, Nine Dragons was their first dim sum.',
            'For me, it was a little piece of those Sundays in Philadelphia’s Chinatown showing up unexpectedly in Sydney.',
            'And if I ever make it back to Sydney, I already know where I’m having lunch.',
          ],
        },
      ],
    }),
    reference('marrickville-hotel', 31, 'Day 3', 'We hadn’t gone to Marrickville for the Marrickville Hotel. We were there for dinner at 20 Chapel. With some time before our reservation, we did what had become increasingly normal during our week in Sydney. We searched for a nearby pub where we could have a beer first. The Marrickville Hotel came back as an option. It was in Marrickville. 20 Chapel was in Marrickville. That sounded close enough.', {
      cardImage: cardImage('marrickville-hotel-card', marrickvilleHotelImage, 'The Reckon crew sharing a drink at Marrickville Hotel'),
      storyTitle: 'A Pint Before the Walk',
      detailLabel: 'Read the Marrickville Hotel Story',
      storySections: [
        {
          heading: 'A Neighborhood Pub',
          paragraphs: [
            'The Marrickville Hotel sat right along the main commercial drag and felt like another traditional neighborhood pub.',
            'Inside, it was dimly lit and unpretentious. We ordered at the bar and then found our way toward the back, where there was a courtyard-like space that wasn’t quite outdoors.',
            'It had walls and a makeshift roof overhead, with red lighting casting a glow over the room. Local knickknacks covered the walls, and picnic-style benches filled the space.',
            'We grabbed one of the tables and settled in for our pre-dinner beer.',
            'It felt local.',
            'That was increasingly becoming something we liked about Sydney’s pubs. Some had famous views or a particular reason for seeking them out. Others were simply neighborhood places where people stopped for a drink.',
            'The Marrickville Hotel felt like the latter.',
          ],
        },
        {
          heading: 'Schooner or Pot?',
          paragraphs: [
            'The Marrickville Hotel also taught us another piece of Australian pub vocabulary.',
            'At the bar, the beer prices were listed by size.',
            'Schooner.',
            'Pot.',
            'We had no idea what that meant.',
            'I believe it was Ben who finally asked the bartender to explain what we were actually ordering.',
            'It was one of those tiny travel moments that doesn’t sound particularly significant until you realize how many of them accumulate during a trip. You arrive somewhere thinking you know how to order a beer, then discover even that comes with local terminology.',
            'By this point we’d already learned that finding a Fosters in Australia was considerably harder than American advertising had led us to believe.',
            'Now we were learning how Australians measured the beers we actually could find.',
          ],
        },
        {
          heading: 'Twenty Minutes Away',
          paragraphs: [
            'Eventually it was time to head to dinner.',
            'That’s when we discovered that our definition of “in Marrickville” had been a little generous.',
            'We plugged 20 Chapel into Google Maps.',
            'More than twenty minutes on foot.',
            'That wasn’t a problem. We had enough time before our reservation, and after eating and drinking our way around Sydney for several days, a walk wasn’t necessarily a bad thing.',
            'We just assumed those twenty minutes would look something like the area around the pub.',
            'They didn’t.',
          ],
        },
        {
          heading: 'Off the Main Drag',
          paragraphs: [
            'We left the Marrickville Hotel and quickly turned away from the main commercial strip.',
            'Storefronts gave way to mostly residential streets.',
            'We kept following Google Maps.',
            'As we got closer to 20 Chapel, the surroundings changed again.',
            'The houses started disappearing and the neighborhood became increasingly commercial and industrial.',
            'Auto body shops.',
            'Construction companies.',
            'Warehouses.',
            'Office and industrial buildings.',
            'Then Google Maps decided that streets were apparently optional.',
            'The route started cutting us through parking lots and into what felt like a dark alley running through the industrial area.',
            'There wasn’t much street lighting.',
            'There weren’t people walking around.',
            'There certainly weren’t rows of restaurants suggesting we’d arrived in some hidden dining district.',
            'I don’t remember Ben’s exact words, but I’m reasonably certain this was around the point where he started questioning how far off the beaten path we were getting and whether we were actually going the right way.',
            'Fair questions.',
            'All we really had was the blue line on Google Maps telling us to keep walking.',
            'So we did.',
          ],
        },
        {
          heading: 'An Oasis in the Desert',
          paragraphs: [
            'Then we came out of the dark alley and onto the next block.',
            'Everything around us still looked industrial.',
            'Auto body shops.',
            'Construction businesses.',
            'Warehouses.',
            'And right in the middle of the block was something that seemed completely out of place.',
            'A sleek black modern building with a glass front.',
            'On the wall:',
            '20 Chapel.',
            'After the dark walk through the industrial area, it looked like an oasis in the desert.',
            'Suddenly Google Maps didn’t seem so crazy.',
            'We had found dinner.',
            'And we had absolutely no idea yet that the restaurant hidden among those warehouses was about to become the crew’s unanimous favorite meal in Sydney.',
          ],
        },
      ],
    }),
    reference('20-chapel', 32, 'Day 3', 'There are restaurants where you remember a particular steak. There are restaurants where you remember the service. There are restaurants where you remember the room, the music or the people you were with. 20 Chapel somehow got all of it right. By the end of our week in Sydney, after some exceptional meals in some exceptional places, there was one thing the crew agreed on unanimously. 20 Chapel was our favorite. Getting there only made the discovery better.', {
      cardImage: cardImage('20-chapel-card', chapelDinnerImage, 'The Reckon crew gathered for dinner at 20 Chapel'),
      storyTitle: 'An Oasis in the Desert',
      detailLabel: 'Read the 20 Chapel Story',
      whatWeRemember: 'Sometimes the best meals are the ones you have to trust the journey to find.',
      recommendation: 'The group favorite of the entire trip.',
      featured: true,
      storySections: [
        {
          heading: 'Trust Google Maps',
          paragraphs: [
            'We had started the evening with a pint at the Marrickville Hotel.',
            'Both places were in Marrickville, so when we originally picked the pub for a pre-dinner drink, we hadn’t given much thought to how far apart they actually were.',
            'Google Maps eventually informed us that dinner was still more than a twenty-minute walk away.',
            'No problem.',
            'We had plenty of time before our reservation.',
            'What we hadn’t anticipated was where those twenty minutes were going to take us.',
            'We left the commercial strip around the Marrickville Hotel and moved into residential streets. As we got closer to the restaurant, the neighborhood changed again.',
            'Auto body shops.',
            'Construction companies.',
            'Warehouses.',
            'Industrial buildings.',
            'Then Google Maps sent us through some dark parking lots and an alley with very little street lighting.',
            'At that point, I’m pretty sure Ben was beginning to question whether we were actually headed toward one of Sydney’s better steakhouses.',
            'We kept following the blue line.',
          ],
        },
        {
          heading: 'An Oasis in the Desert',
          paragraphs: [
            'Then we came out of the darkness and onto the block where Google Maps insisted our restaurant was located.',
            'In the middle of all those industrial buildings was something completely different.',
            'A sleek, modern black building with a glass front.',
            '20 Chapel.',
            'After the walk we’d just taken, it looked like an oasis in the desert.',
            'We opened the door.',
            'The contrast continued.',
          ],
        },
        {
          heading: '90s Hip-Hop and Australian Wagyu',
          paragraphs: [
            'We were immediately greeted warmly by the staff.',
            'There was 90s hip-hop playing in the background, which told us almost instantly that this wasn’t going to be a traditional steakhouse experience.',
            'The room was stylish and modern without feeling formal. A small bar sat off to the left, and we were led toward our table in the back right corner.',
            'On the other side of a glass partition was the open kitchen.',
            'We had an excellent view of it from our table.',
            'There was only one table in the restaurant I would have rather had.',
            'It was actually inside the kitchen.',
            'Unfortunately, a group of guys was already sitting there.',
            'I made a mental note.',
            'If we ever make it back to 20 Chapel, that’s our table.',
          ],
        },
        {
          heading: 'Everybody Gets Their Own',
          paragraphs: [
            'We didn’t overcomplicate the steaks.',
            'Everyone ordered the cut they wanted.',
            'Australian Wagyu.',
            'The server helped us choose a collection of sides for the table, which turned out to be exactly the right approach.',
            'And then everything just worked.',
            'The steaks were excellent. The sides complemented them perfectly. The service was top-notch.',
            'But isolating any one of those things misses why we liked 20 Chapel so much.',
            'It was the entire experience.',
            'The music.',
            'The room.',
            'The open kitchen.',
            'The staff.',
            'The Australian Wagyu.',
            'The sides being passed around the table.',
            'The atmosphere.',
            'Nothing felt overly formal, and nothing felt casual enough to diminish how seriously they took the food.',
            'It was simply a place where we wanted to keep sitting.',
          ],
        },
        {
          heading: 'The Unanimous Pick',
          paragraphs: [
            'We ate extremely well in Sydney.',
            'Some of our meals came with famous chefs. Some came with spectacular views. Some were restaurants we had chosen long before we ever boarded the flight to Australia.',
            '20 Chapel required us to walk through dark parking lots past auto body shops and warehouses.',
            'And it was everyone’s favorite.',
            'Unanimously.',
            'That doesn’t happen often with a group.',
            'Usually someone prefers another steak, another restaurant, another atmosphere or another night.',
            'Not this time.',
            'When we talked about the meals afterward, 20 Chapel was the one.',
            'And I think that’s because we weren’t really voting on the best steak.',
            'We were remembering the night.',
          ],
        },
        {
          heading: 'Next Time, the Kitchen Table',
          paragraphs: [
            'When dinner was finished, nobody suggested retracing our route through Marrickville on foot.',
            'We called two Ubers.',
            'After everything it had taken to get there, the trip back to the hotel was remarkably simple.',
            'But I left 20 Chapel with something I hadn’t expected when we first emerged from that dark alley.',
            'A reason to come back.',
            'If another Reckon trip eventually brings us to Sydney, I already know where one of the dinners should be.',
            'And this time, I’m reserving the table in the kitchen.',
          ],
        },
      ],
    }),
    reference('4-pines-manly', 41, 'Day 4', 'By the time we walked into 4 Pines, we had already done what we’d come to Manly to do. We had taken the ferry across Sydney Harbour, walked down the promenade from the wharf to Manly Beach, wandered along the beachfront and gotten a feel for this completely different side of Sydney. Now it was getting close to dusk. Before catching the ferry back, we decided we needed two things. A beer and some food. 4 Pines was right across from the ferry wharf. Decision made.', {
      cardImage: cardImage('4-pines-card', fourPinesImage, 'The Reckon crew having lunch at 4 Pines Brew Pub in Manly'),
      storyTitle: 'The Soundtrack to Manly',
      detailLabel: 'Read the 4 Pines Story',
      storySections: [
        {
          heading: 'Upstairs',
          paragraphs: [
            'The brewpub was on the second floor, and it was crowded when we arrived.',
            'There wasn’t a table available, so the whole crew did what you do in that situation.',
            'We ordered drinks and hovered near the bar.',
            'Nobody was in a particular hurry. The ferry wasn’t going anywhere permanently, and after walking around Manly, standing with a beer for a while wasn’t exactly a hardship.',
            'Eventually a table opened.',
            'We moved quickly.',
            'It was ours.',
          ],
        },
        {
          heading: 'An Australian Beach Bar',
          paragraphs: [
            'The easiest way I can describe 4 Pines is that the bones of the place felt familiar.',
            'It had all the trappings of a microbrewery we might have walked into back in the States.',
            'But the energy was completely different.',
            'It felt like what you would imagine a stereotypical Australian beach bar should look, feel and sound like.',
            'Especially sound like.',
            'Men at Work came through the speakers.',
            'Then more 80s music.',
            'Then, seemingly, Men at Work again.',
            'I’m not sure whether the playlist was actually on a loop or whether it just felt that way, but nobody at 4 Pines seemed particularly concerned about leaning into the Australian beach-bar stereotype.',
            'Neither were we.',
            'We were in Manly.',
            'We had just come from the beach.',
            'It worked.',
          ],
        },
        {
          heading: 'Beer, Wings and Whatever Else',
          paragraphs: [
            'Once we secured the table, we ordered food.',
            'Wings and other assorted bar food started arriving along with the beers.',
            'I couldn’t tell you which beer was the best.',
            'I couldn’t tell you which plate disappeared first.',
            'And that’s probably the most accurate review of the food I can give.',
            'It was good bar food and cold beer at exactly the point in the day when we wanted good bar food and cold beer.',
            'The food wasn’t why we remembered 4 Pines.',
            'Neither, surprisingly, was the beer.',
            'It was the vibe.',
          ],
        },
        {
          heading: 'The Sound of Manly',
          paragraphs: [
            'Some places stick with you because of something extraordinary.',
            'Others somehow capture exactly what you imagined a place might feel like before you ever got there.',
            '4 Pines did that for Manly.',
            'A crowded second-floor brewery.',
            'The whole crew gathered around a table we’d waited to grab.',
            'Beers in front of us.',
            'The beach a short walk away.',
            '80s Australian music coming through the speakers.',
            'Daylight disappearing outside.',
            'It was almost too perfectly on theme.',
            'And that was exactly what made it fun.',
          ],
        },
        {
          heading: 'Last Stop Before Sydney',
          paragraphs: [
            'Eventually it was time to head back across the harbor.',
            'We finished the food, finished the beers and walked back toward the ferry wharf.',
            'When we’d arrived in Manly, there was still plenty of daylight.',
            'Now dusk was settling in.',
            'By the time the ferry carried us back across Sydney Harbour, it would be completely dark.',
            'That would give us an entirely different view of the city than the one we’d seen on the way over.',
            'But that is another story.',
            'For our last stop in Manly, 4 Pines had given us exactly what we needed.',
            'A table.',
            'A beer.',
            'Something to eat.',
            'And apparently the official soundtrack to Australia.',
          ],
        },
      ],
    }),
    reference('mountain-culture', 60, 'Day 6', 'By the time we left Featherdale, we’d had a pretty full day. Lincoln’s Rock. Echo Point. Katoomba Cascades. Scenic World. Koalas, kangaroos, wallabies, wombats and some surprisingly aggressive pigeons at Featherdale. We’d been on the move since Tom picked us up that morning. The scheduled part of the tour was essentially over. Then Tom had an idea. There was a brewery he thought we’d like. Of course there was.', {
      cardImage: cardImage('mountain-culture-card', mountainCultureImage, 'A local beer at Mountain Culture Beer Co. in the Blue Mountains'),
      storyTitle: 'One More Stop',
      detailLabel: 'Read the Mountain Culture Story',
      detailSectionId: 'activities',
      detailEntityId: 'blue-mountains',
      detailStoryPageSlug: 'mountain-culture',
      whatWeRemember: 'Great beer, a welcoming room, and the wish that we had more time.',
      recommendation: 'One of our first stops if we return to the Blue Mountains.',
      storySections: [
        {
          heading: 'An American in Katoomba',
          paragraphs: [
            'As we headed there, Tom gave us some of the backstory.',
            'Mountain Culture had been started by an American who had brought some of the American craft-brewing culture with him to the Blue Mountains.',
            'Tom also told us about one of their beers in particular.',
            'Status Quo.',
            'It had become one of the brewery’s signature beers and had built quite a reputation in Australia.',
            'After a week of trying different Australian beers everywhere we went, Tom apparently understood his audience pretty well.',
            'There wasn’t much debate about making one more stop.',
          ],
        },
        {
          heading: 'Startups Start in Garages',
          paragraphs: [
            'You hear stories all the time about startups beginning in garages.',
            'Mountain Culture felt like someone had taken that idea literally.',
            'We pulled up and found what essentially looked like a garage with a bar inside it.',
            'It wasn’t polished or elaborate.',
            'It didn’t need to be.',
            'There was a bar where you could order beer and a patio out back with tables.',
            'That was about all we required.',
            'We went inside.',
          ],
        },
        {
          heading: 'Status Quo',
          paragraphs: [
            'Most of us ordered the beer Tom had been telling us about.',
            'Status Quo.',
            'Then we carried our beers out back and found a table on the patio.',
            'After the day we’d had, sitting down with a cold beer felt pretty good.',
            'And the beer lived up to the recommendation.',
            'Status Quo was genuinely great.',
            'Sometimes a stop works because of the place.',
            'Sometimes because of the timing.',
            'This one had both.',
            'We weren’t rushing to another overlook.',
            'Nobody needed to make a train.',
            'There wasn’t another attraction waiting for us.',
            'We could just sit outside, drink a good beer and talk about everything we’d done that day.',
            'It felt like the perfect cap to the tour.',
            'Still no Fosters.',
          ],
        },
        {
          heading: 'One Last Ride With Tom',
          paragraphs: [
            'Eventually we finished our beers and climbed back into the Anderson’s Tours van.',
            'There was still one final drive.',
            'Tom took us toward Sydney Olympic Park.',
            'After spending the entire day together, this was where we’d eventually part ways.',
            'By then, Tom had become much more than the guy driving the van.',
            'He had talked us through Sydney’s growth as we drove out of the city.',
            'He’d taken us to Lincoln’s Rock, where the wind launched his hat completely over the cliff before a thermal somehow delivered it back.',
            'He’d given us the stories and context behind the landscape.',
            'He’d walked through the rainforest with us and helped us track down the lyrebirds we’d heard moving through the woods.',
            'And when the planned itinerary was essentially finished, he’d decided we needed a beer.',
            'That last recommendation had been a pretty good one.',
          ],
        },
        {
          heading: 'Olympic Park',
          paragraphs: [
            'Tom dropped us at Sydney Olympic Park, where we could catch a ferry back toward Circular Quay.',
            'We said goodbye, thanked him for the day and headed for the ferry.',
            'It was an interesting place for our time with Tom to end.',
            'That morning, he’d picked us up in the middle of Sydney and driven us west through what seemed like an endless succession of suburbs.',
            'Now we were on the western side of the city preparing to return a completely different way.',
            'By water.',
          ],
        },
        {
          heading: 'Back to the Quay',
          paragraphs: [
            'We boarded the ferry at Olympic Park and started the ride back toward Circular Quay.',
            'There was something fitting about finishing the day that way.',
            'That morning we’d watched Sydney slowly disappear through the windows of Tom’s van.',
            'The suburbs had become less dense.',
            'The buildings had disappeared.',
            'The terrain had started climbing.',
            'Eventually we’d found ourselves standing on an exposed rock above an enormous valley in the Blue Mountains.',
            'Now the process was reversing.',
            'Except we weren’t watching it through the windshield of a van.',
            'We were coming home on the water.',
            'There were no more stops to make.',
            'No cliffs to stand on.',
            'No railways to ride backward up a mountain.',
            'No koalas to pet.',
            'No kangaroos to hand-feed.',
            'No pigeons trying to steal the leftovers.',
            'Just the crew on a ferry after a very full Friday, heading back toward Sydney.',
          ],
        },
        {
          heading: 'A Pretty Good Last Full Day',
          paragraphs: [
            'We still needed dinner when we got back.',
            'It was Friday night in Sydney, so restaurants were busy. We eventually found somewhere to eat, but the meal itself wasn’t particularly memorable.',
            'That was fine.',
            'We had enough memories from the day.',
            'We’d stood above the Blue Mountains and walked through the rainforest below them.',
            'We’d tracked lyrebirds through the woods.',
            'We’d ridden an impossibly steep railway in both directions.',
            'We’d seen the Three Sisters from multiple perspectives.',
            'We’d gotten close enough to a koala to discover just how soft its fur was.',
            'And we’d finished it all sitting on the back patio of a brewery with a Status Quo in our hands.',
            'Then we caught a ferry home.',
            'For our last full day in Australia, that was more than enough.',
          ],
        },
      ],
    }),
    reference('bar-torino', 53, 'Day 5', 'We didn’t actually plan to go to Bar Torino. We were in Double Bay for dinner at Margaret. Double Bay is one of Sydney’s more polished, upscale neighborhoods, and we’d arrived for an early reservation right around the time the restaurant was supposed to open. Apparently we were a little too early. Margaret wasn’t open yet.', {
      cardImage: cardImage('bar-torino-card', barTorinoImage, 'The Reckon crew gathered for cocktails at Bar Torino'),
      storyTitle: 'Too Early for Dinner',
      detailLabel: 'Read the Bar Torino Story',
      storySections: [
        {
          heading: 'Down the Block',
          paragraphs: [
            'We had assumed we’d simply sit at Margaret’s bar, have a drink and wait while they finished getting our table ready.',
            'Instead, we found the staff still setting up the restaurant.',
            'They were nice enough to offer an alternative.',
            'Their sister bar, Bar Torino, was just down the block.',
            'Go there.',
            'Have a drink.',
            'Come back.',
            'That sounded like a reasonable plan.',
          ],
        },
        {
          heading: 'Down the Stairs',
          paragraphs: [
            'Bar Torino was below street level.',
            'We headed downstairs and walked into a place that looked brand new but somehow felt much older.',
            'We were initially the only people there.',
            'The room had an old-school speakeasy quality to it. It was stylish without feeling overly formal, tucked away beneath the streets of Double Bay and removed from whatever was happening outside.',
            'Then there was the music.',
            'Laid-back jazz was pumping through the speakers.',
            'Not loudly.',
            'Just enough to establish exactly what kind of place we’d walked into.',
            'The whole thing felt almost deliberately cinematic.',
          ],
        },
        {
          heading: 'One Drink Became Two',
          paragraphs: [
            'We sat down and ordered.',
            'Most of the crew went with cocktails, which was clearly the appropriate decision for the surroundings.',
            'I ordered a beer.',
            'Somebody has to maintain standards.',
            'We weren’t there long. Maybe thirty minutes altogether.',
            'But apparently thirty minutes was enough time for one drink to become two.',
            'There was no rush. Margaret was just down the street, and for the moment we essentially had this little underground bar to ourselves.',
            'So we stayed for another.',
          ],
        },
        {
          heading: 'The Place We Weren’t Looking For',
          paragraphs: [
            'Bar Torino wasn’t one of the restaurants or bars we’d researched before coming to Sydney.',
            'It wasn’t somewhere we’d built into the itinerary.',
            'We found it because we showed up too early for dinner.',
            'That’s really the entire story.',
            'And that’s also why I remember it.',
            'We walked into Margaret expecting to wait at the bar. Instead, someone pointed us down the street, we descended a staircase below Double Bay and unexpectedly found ourselves sitting in what felt like a modern version of an old speakeasy with jazz playing through the speakers.',
            'Two drinks later, it was time for dinner.',
            'We climbed back up to street level and walked back toward Margaret.',
            'This time, they were ready for us.',
          ],
        },
      ],
    }),
    reference('margaret-sydney', 54, 'Day 5', 'There are plenty of ways to choose a restaurant when you’re traveling. Location. Chef. Cuisine. Recommendation from someone you trust. Then there is another method. Find out that a restaurant has been ranked the number two steakhouse in the world and decide there is really no reasonable argument for not going. That’s how Margaret made our Sydney list.', {
      cardImage: cardImage('margaret-card', margaretDinnerImage, 'The Reckon crew seated together for dinner at Margaret'),
      storyTitle: 'Number Two in the World',
      detailLabel: 'Read the Margaret Story',
      whatWeRemember: 'Exceptional food, outstanding hospitality, and a memorable final dinner.',
      featured: true,
      storySections: [
        {
          heading: 'Number Two',
          paragraphs: [
            'In 2025, Margaret was ranked number two on the World’s 101 Best Steak Restaurants list, making it the highest-ranked Australian restaurant on the list.',
            'That got our attention.',
            'We were already planning to eat plenty of Australian beef while we were in Sydney, and getting a reservation at one of the world’s most highly regarded steakhouses seemed like an obvious addition to the trip.',
            'Getting the reservation wasn’t particularly easy.',
            'We eventually found one, although there was a catch.',
            'We weren’t sitting inside.',
          ],
        },
        {
          heading: 'Too Early',
          paragraphs: [
            'Margaret is in Double Bay, a polished, upscale part of Sydney that felt noticeably different from some of the neighborhoods we’d explored earlier in the week.',
            'Our reservation was early, right around opening.',
            'We actually arrived before Margaret was ready for us.',
            'We had assumed we’d sit at the bar and have a drink while the restaurant finished setting up, but they weren’t even open yet.',
            'The staff pointed us down the block toward their sister bar, Bar Torino.',
            'Thirty minutes and two drinks later, we returned.',
            'This time Margaret was ready.',
          ],
        },
        {
          heading: 'Dinner on the Sidewalk',
          paragraphs: [
            'Our table was outside.',
            'Not an elaborate enclosed terrace or some separate outdoor dining room.',
            'The tables were arranged along the sidewalk.',
            'Initially, that felt like a bit of a compromise for a reservation we’d worked pretty hard to get.',
            'But the service never felt compromised.',
            'The staff took care of the outdoor tables with the same attention you’d expect inside the restaurant.',
            'What changed was the atmosphere.',
            'Instead of the restaurant itself providing the entire backdrop, Double Bay did.',
            'People walked past throughout dinner. The neighborhood stayed active around us. We had front-row seats to an evening in one of Sydney’s chicest neighborhoods while eating at one of its most celebrated restaurants.',
            'As the evening went on, the temperature started dropping.',
            'Fortunately, they had propane heaters.',
            'Those became increasingly appreciated as dinner continued.',
          ],
        },
        {
          heading: 'The Ribeye',
          paragraphs: [
            'Everyone chose their own steak.',
            'Brandon A. and I found another option.',
            'There was a ribeye intended for two.',
            'That sounded better.',
            'We split it.',
            'The staff helped us choose a collection of sides for the table, and soon we had exactly what we’d come for: Australian beef, shared sides and another table full of food being passed around among the crew.',
            'The ribeye was excellent.',
            'Margaret absolutely delivered on the steak.',
            'The problem wasn’t Margaret.',
            'The problem was 20 Chapel.',
          ],
        },
        {
          heading: 'An Unfair Comparison',
          paragraphs: [
            'Earlier in the trip, we’d walked through dark streets and industrial parking lots in Marrickville to a restaurant surrounded by warehouses, construction companies and auto body shops.',
            'Nobody had arrived at 20 Chapel carrying expectations created by a global ranking.',
            'Then it became everyone’s favorite meal of the trip.',
            'Margaret had to follow that.',
            'And that’s an unfair assignment for almost any restaurant.',
            'The steak at Margaret was great. The service was excellent. The sides worked. Nothing about the meal gave us reason to question why the restaurant had earned such an extraordinary reputation.',
            'But we were outside on the sidewalk on an increasingly cool evening.',
            'At 20 Chapel, we’d been inside with 90s hip-hop playing, an open kitchen beside us and an atmosphere that seemed perfectly matched to the food.',
            'Margaret may have been ranked number two in the world.',
            'Among the Reckon Crew that week, 20 Chapel still had the title.',
          ],
        },
        {
          heading: 'Dessert and a Flat White',
          paragraphs: [
            'We didn’t rush away after the steaks.',
            'We stayed for dessert.',
            'And I finished the meal with a flat white.',
            'After spending the week discovering Australian pubs, Australian Wagyu, kangaroo, emu, crocodile and an increasingly impressive collection of local beer, ending dinner with one of Australia’s great contributions to coffee culture seemed appropriate.',
            'Outside, Double Bay continued moving around us.',
            'The propane heaters kept the cooling evening manageable.',
            'And we got to sit there a little longer.',
            'Margaret had been one of the restaurants we most wanted to experience before we arrived in Sydney.',
            'It lived up to its reputation.',
            'It just also reminded us of something these trips keep proving.',
            'The place with the biggest reputation isn’t always the place you end up talking about the most.',
          ],
        },
      ],
    }),
    reference('sydney-pub-crawl', 70, 'All week', 'At some point during our week in Sydney, Brandon L. did what Brandon L. does particularly well. He started searching the internet. The assignment was simple enough: find us some pubs worth visiting around The Rocks. What emerged wasn’t an official tour, organized crawl or carefully timed itinerary. It was just a collection of places that looked interesting, arranged into something resembling a route. That was good enough for us. It was Monday night. The entire crew was together. We started walking.', {
      cardImage: cardImage('sydney-pub-crawl-card', pubTableImage, 'The Reckon crew gathered around an outdoor pub table during their Sydney pub crawl'),
      storyTitle: 'Brandon’s Internet Research in Action',
      detailLabel: 'Read the Sydney Pub Crawl Story',
      whatWeRemember: 'Historic pubs, rooftop bars, harbor-side pints, and a final tally that nobody could quite remember.',
      storySections: [
        {
          heading: 'Stop One: Hart’s Pub',
          paragraphs: [
            'Hart’s was our first stop, and it immediately felt like the neighborhood pub of the night.',
            'The downstairs bar was small and most of the tables were occupied by people who looked like locals. There wasn’t really anywhere for seven of us to linger at the bar anyway.',
            'You ordered your beer and moved along.',
            'So we took our first round upstairs.',
            'We found an outdoor area with big round tabletops mounted on old wooden barrels.',
            'Nobody else was up there.',
            'Downstairs, the regulars had their neighborhood pub.',
            'Upstairs, we had our own private pub crawl headquarters.',
            'We settled around one of the barrel tables with our first beers.',
            'Then someone asked the obvious question.',
            'Do they have Fosters?',
            'They did not.',
            'This was surprising.',
            'We had spent decades back home being told through advertising that Fosters was essentially the national beverage of Australia. Now we were sitting in an Australian pub that specializes in Australian craft beer, and apparently nobody had gotten the memo. Hart’s actually pours an all-Australian rotating craft-beer lineup, which in retrospect made what happened next even more appropriate.',
            'We ordered something local instead.',
            'Then we ordered another.',
            'Our pub crawl officially began with two pints at stop number one.',
            'We weren’t exactly setting a blistering pace.',
          ],
        },
        {
          heading: 'Stop Two: Eat the Coat of Arms',
          paragraphs: [
            'Eventually we left Hart’s and continued toward the Australian Heritage Hotel.',
            'This one had made Brandon’s list for a very specific reason.',
            'Pizza.',
            'Not ordinary pizza.',
            'The Australian Heritage is known for its Coat of Arms pizza, topped with kangaroo and emu, as well as a crocodile pizza.',
            'There was no chance we were walking past that.',
            'Inside was another traditional Australian pub. We went to the bar, ordered another round and added both pizzas.',
            'Then we carried our drinks outside and found a table on the sidewalk beside the entrance.',
            'The pizzas went into the middle.',
            'Everybody got a slice.',
            'Kangaroo.',
            'Emu.',
            'Crocodile.',
            'Nobody hesitated much. By this point in our travels together, everyone knows there will eventually be something put in front of them that they weren’t expecting to eat.',
            'The surprise was how unsurprising it tasted.',
            'Once everything was chopped, seasoned and put on a pizza, the meats weren’t radically different from familiar toppings back home. They landed somewhere around the general territory of sausage, pepperoni or ground beef.',
            'Eating Australia’s coat of arms sounded considerably more adventurous than it actually tasted.',
            'Another pint disappeared.',
            'Still no Fosters.',
          ],
        },
        {
          heading: 'Stop Three: Where Is Everybody?',
          paragraphs: [
            'The Glenmore was next.',
            'We walked into the street-level bar and found it virtually empty.',
            'For a Monday night, that wasn’t necessarily shocking.',
            'Still, after Brandon’s first two selections had worked out pretty well, walking into an empty pub raised a few questions.',
            'Then we found the stairs.',
            'They were tight, narrow and winding.',
            'We started climbing.',
            'And climbing.',
            'When we reached the rooftop, we discovered where everybody was.',
            'The rooftop wasn’t packed, but compared with downstairs it felt like we’d entered a completely different pub.',
            'Better yet, we had no problem finding seats right along the railing.',
            'Then we looked out.',
            'Across the harbor was the Sydney Opera House.',
            'The Glenmore is known for that rooftop and its sweeping views across Sydney Harbour, including both the Opera House and Harbour Bridge.',
            'Suddenly Brandon’s selection made complete sense.',
          ],
        },
        {
          heading: 'One More Pint With Sydney',
          paragraphs: [
            'We ordered another round.',
            'Something local.',
            'Obviously.',
            'By now the Fosters search had become a running joke. The supposedly ubiquitous Australian beer remained nowhere to be found, while actual Australian beers kept appearing everywhere we went.',
            'We were beginning to get the message.',
            'At the Glenmore, though, nobody was particularly concerned about what was in the glass.',
            'The view was the reason we remember that stop.',
            'We sat along the rail with our beers and looked across at the Opera House illuminated against the night.',
            'There are pubs where you remember what you drank.',
            'There are pubs where you remember who you talked to.',
            'At the Glenmore, I remember where we were sitting and what was across the harbor.',
            'That was enough.',
          ],
        },
        {
          heading: 'Almost One More',
          paragraphs: [
            'Eventually we finished our beers and started walking back.',
            'Apparently three pubs, several pints and two pizzas containing a small collection of Australian wildlife hadn’t completely exhausted our ambition.',
            'There was potentially one more stop.',
            'The Hero of Waterloo.',
            'As we walked past, though, it looked like they were closing down for the night.',
            'We never even went inside.',
            'We just kept walking.',
            'In hindsight, that was probably fine.',
            'The Hero has been standing in The Rocks since the 1840s.',
            'Presumably it can wait for us to come back.',
          ],
        },
        {
          heading: 'Brandon’s Pub Crawl',
          paragraphs: [
            'There was nothing particularly sophisticated about the night.',
            'Brandon searched the internet.',
            'We trusted the results.',
            'That took us from a neighborhood craft-beer pub where we essentially had the upstairs to ourselves, to kangaroo and emu pizza on a sidewalk, to a rooftop overlooking the Sydney Opera House.',
            'Along the way we drank local beer, ate crocodile, wandered through The Rocks and repeatedly failed to find a Fosters.',
            'And everyone was there.',
            'That’s probably the part that matters most.',
            'No reservations.',
            'No conference agenda.',
            'No scheduled experience.',
            'Just seven of us walking from pub to pub through one of Sydney’s oldest neighborhoods on a Monday night, following a route Brandon had assembled on his phone.',
            'Sometimes the itinerary doesn’t need to be any more complicated than that.',
            'Pick the first pub.',
            'Order a pint.',
            'And see where the night takes you.',
          ],
        },
      ],
    }),
  ],
  hotels: [
    reference('w-sydney', 1, 'All week', 'W Sydney gave the crew a Darling Harbour home base within reach of the conference and the city.', {
      cardImage: cardImage('w-sydney-hotel-card', wSydneyHotelImage, 'The W Sydney hotel beside Darling Harbour', 'center center', 'portrait'),
      whyWeWent: 'Its location connected the formal conference schedule with ferry rides, dinners, walks, and nights around the harbor.',
      whatWeRemember: 'Many of the best days ended with a long walk back together.',
      featured: true,
    }),
  ],
  crew: legacy.attendees,
  meritBadges: legacy.achievements,
  gallery: legacy.media.map((item, index) => ({
    id: `sxsw-sydney-media-${index + 1}`,
    type: item.type || 'image',
    src: item.src,
    alt: item.alt,
    caption: item.caption,
    capturedAt: item.capturedAt,
    orientation: item.aspectRatio && item.aspectRatio < 1 ? 'portrait' : 'landscape',
  })),
  tags: legacy.tags,
  closingReflection: 'SXSW Sydney gave us a reason to visit one of the world’s great cities. The conference filled our days, but Sydney filled everything else, from the harbor and coastal walks to award-winning dinners, ferries, live music, hidden gems, and the moments between the itinerary. We experienced the city the way we hope to experience every destination: as a team. That’s the Reckon Way.',
});

const blueMountainsActivity = sxswSydney2025.activities
  .find((item) => item.entityId === 'blue-mountains');
const scenicWorldActivity = sxswSydney2025.activities
  .find((item) => item.entityId === 'scenic-world');
const featherdaleActivity = sxswSydney2025.activities
  .find((item) => item.entityId === 'featherdale-sydney-wildlife-park');
const mountainCultureRestaurant = sxswSydney2025.restaurants
  .find((item) => item.entityId === 'mountain-culture');

blueMountainsActivity.storyPages = [
  {
    slug: 'overview',
    label: 'Overview',
    title: 'The Blue Mountains Excursion',
    date: 'Day 6 · Our last full day in Australia',
    summary: 'One private tour connected the Blue Mountains, Scenic World, Featherdale, Mountain Culture, and the ferry ride home.',
    sections: [
      {
        heading: 'The Blue Mountains Excursion',
        subheading: 'Four Stories. One Very Full Day.',
        date: 'Day 6 · Blue Mountains and Western Sydney',
        paragraphs: [
          'Our last full day in Australia became a journey through four connected experiences, each revealing a different side of the country beyond central Sydney.',
          'Tom from Anderson’s Tours led us west through the expanding suburbs and into the Blue Mountains. We stood above the valley at Lincoln’s Rock and Echo Point, descended into the rainforest at Scenic World, met koalas and kangaroos at Featherdale, and finished the tour over Status Quo beers at Mountain Culture.',
          'The return journey completed the arc. Tom dropped us at Sydney Olympic Park, where we said goodbye and boarded a ferry back toward Circular Quay.',
          'Together, these four chapters tell the full story of the excursion in the order we experienced it.',
        ],
      },
    ],
  },
  {
    slug: 'beyond-sydney',
    label: 'Part 1',
    title: blueMountainsActivity.storyTitle,
    date: 'Morning · Into the Blue Mountains',
    summary: blueMountainsActivity.eventDescription,
    sections: blueMountainsActivity.storySections,
  },
  {
    slug: 'scenic-world',
    label: 'Part 2',
    title: scenicWorldActivity.storyTitle,
    date: 'Late morning · Scenic World',
    summary: scenicWorldActivity.eventDescription,
    sections: scenicWorldActivity.storySections,
  },
  {
    slug: 'featherdale',
    label: 'Part 3',
    title: featherdaleActivity.storyTitle,
    date: 'Afternoon · Featherdale Wildlife Park',
    summary: featherdaleActivity.eventDescription,
    sections: featherdaleActivity.storySections,
  },
  {
    slug: 'mountain-culture',
    label: 'Part 4',
    title: mountainCultureRestaurant.storyTitle,
    date: 'Late afternoon · Katoomba to Circular Quay',
    summary: mountainCultureRestaurant.eventDescription,
    sections: mountainCultureRestaurant.storySections,
  },
];

blueMountainsActivity.storyLinks = blueMountainsActivity.storyPages.map((page) => ({
  label: page.slug === 'overview' ? 'Read Overview' : `${page.label}: ${page.title}`,
  storyPageSlug: page.slug,
}));

const imageBearingSections = ['sessions', 'venues', 'places', 'restaurants', 'hotels', 'activities'];
const pageImages = [
  { image: sxswSydney2025.heroImage, caption: sxswSydney2025.subtitle },
  ...imageBearingSections.flatMap((section) => sxswSydney2025[section].map((item) => ({
    image: item.cardImage,
    caption: item.eventDescription,
  }))),
];
const galleryBySource = new Map(sxswSydney2025.gallery.map((image) => [image.src, image]));

pageImages.forEach(({ image, caption }) => {
  if (!image || galleryBySource.has(image.src)) return;
  galleryBySource.set(image.src, { ...image, caption: image.caption || caption });
});

sxswSydney2025.gallery = [...galleryBySource.values()];

export default sxswSydney2025;
